import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    LayoutGrid, Plus, X, ChevronLeft, ChevronRight, Users, MessageSquare,
    Paperclip, Calendar as CalendarIcon, Tag, Trash2, Edit2, MoreHorizontal,
    MoveRight, GripVertical, Send, Pencil, Check, Loader2, Circle,
    Search, Maximize2, Minimize2, Download, FileText, LogOut,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PanelShell from './PanelShell';
import DocumentPreviewModal from '../DocumentPreviewModal';
import AttachPickerModal from '../AttachPickerModal';
import Modal from '../ui/Modal';
import RichTextEditor from '../RichTextEditor';
import { avatarInitials } from '../../utils/avatar';
import SharedSpaceDialog from '../SharedSpaceDialog';
import { debounce } from '../../utils/debounce';
import { boardService } from '../../services/boardService';
import { documentService } from '../../services/documentService';
import { parseServerDate } from '../../utils/datetime';
import useAuth from '../../hooks/useAuth';
import useDragAutoScroll from '../../hooks/useDragAutoScroll';
import { useLanguage } from '../../i18n';
import { prefixedUserErrorMessage, userErrorMessage } from '../../utils/userError';

// Splits a translated string around a single {name}-style placeholder so the
// interpolated value can be wrapped in its own JSX element (e.g. a bold <span>)
// instead of being flattened into plain text by t()'s string-only interpolation.
const T_SPLIT_MARK = '\uE000';
const tSplit = (t, key, fallback, varName) => {
    const marked = t(key, fallback, { [varName]: T_SPLIT_MARK });
    const idx = marked.indexOf(T_SPLIT_MARK);
    if (idx === -1) return [marked, ''];
    return [marked.slice(0, idx), marked.slice(idx + T_SPLIT_MARK.length)];
};

/**
 * How a ticket's author is displayed (PO #415 Option B).
 *
 * `board_tickets.created_by` is `ON DELETE SET NULL`, so when a collaborator
 * deletes their PAI account the ticket they wrote **stays on the board** and its
 * author goes NULL. That null IS the tombstone, and it must read as **"Deleted
 * user"** — never blank, never a raw uid, never the `'U'` placeholder (#247).
 *
 * Returns `null` only when the ticket itself is not loaded yet, so a caller can
 * render nothing rather than flash "Deleted user" at an unresolved ticket.
 * A ticket that genuinely records no author is indistinguishable from one whose
 * author was deleted — deliberately, because to a reader they are the same fact.
 *
 * Exported for unit testing: the whole point of the card is the NULL branch, and
 * proving it through a full modal render would be the least direct way to do it.
 */
export const ticketCreatorLabel = (ticket, t) => {
    if (!ticket) return null;
    return ticket.created_by_name
        || t('brdTicketAuthorDeleted', 'Deleted user');
};

// ─────────────────────────────────────────────────────────────────────────
// Small reusable bits
// ─────────────────────────────────────────────────────────────────────────

// The 9 base colors offered when creating/editing a board label (Board labels #85;
// Labels Ui update #289 added "Normal Blue" #3b82f6 alongside the existing "Clear
// Blue" #0ea5e9). MUST stay mirrored byte-for-byte with the BE palette
// (BE/app/services/board_service.py LABEL_PALETTE) so a PAI-created label is
// visually identical to one made here.
const LABEL_COLORS = [
    '#ef4444', '#f97316', '#f59e0b', '#10b981',
    '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899',
];
// Create-form default — indigo, matching BE DEFAULT_LABEL_COLOR (index-independent
// so inserting a palette color can never silently change the default).
const DEFAULT_LABEL_COLOR = '#6366f1';

// PAI design system: label chips are one of the three documented FILLED exceptions to
// the accent rule (the handoff renders `Fase 1` as #052210 on green and `Bug` as white
// on red). Our label colours are user-chosen hexes, so the foreground is derived from
// the swatch's relative luminance instead of hard-coded per label: a bright fill takes
// near-black ink, a dark fill takes white. Pure + presentational — the stored hex (the
// BE-mirrored #289 contract) is never touched.
const labelInkFor = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!m) return '#ffffff';
    const int = parseInt(m[1], 16);
    const srgb = [(int >> 16) & 255, (int >> 8) & 255, int & 255].map((v) => {
        const c = v / 255;
        return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    const luminance = 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
    return luminance > 0.42 ? '#052210' : '#ffffff';
};

// Board Bugs #248 — every board timestamp is rendered through `parseServerDate`.
// board_tickets.due_date / board_ticket_comments.created_at are NAIVE `timestamp`
// columns (UTC, serialized by isoformat() with no tz marker), and `new Date(naive)`
// parses that as the browser's LOCAL time — so the raw UTC clock value was shown
// verbatim (a Bangkok/UTC+7 comment at 23:37 rendered as 4:36 PM). parseServerDate
// treats a naive timestamp as UTC then renders local; already-zoned strings (the
// optimistic client-side `toISOString()` placeholders) pass through unchanged.
const formatDateChip = (iso) => {
    if (!iso) return null;
    try {
        const d = parseServerDate(iso);
        if (!d) return null;
        const now = new Date();
        const sameYear = d.getFullYear() === now.getFullYear();
        return d.toLocaleDateString(undefined, {
            month: 'short', day: 'numeric',
            year: sameYear ? undefined : 'numeric',
        });
    } catch { return null; }
};

// Comment section Boards #241 — a comment timestamp must show the exact date AND
// time ("July 24, 14:34" instead of just "July 24") so the thread reads as a real
// conversation. Date part is long-month + day (+ year only when not the current
// year); time is HH:MM in the viewer's locale.
const formatCommentTimestamp = (iso) => {
    if (!iso) return null;
    try {
        const d = parseServerDate(iso);   // #248: naive server UTC → viewer-local
        if (!d || Number.isNaN(d.getTime())) return null;
        const now = new Date();
        const sameYear = d.getFullYear() === now.getFullYear();
        const datePart = d.toLocaleDateString(undefined, {
            month: 'long', day: 'numeric',
            year: sameYear ? undefined : 'numeric',
        });
        const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        return `${datePart}, ${timePart}`;
    } catch { return null; }
};

// (Board UI consistency #258: the local `initialsFor` helper was DELETED. It took
// the first letter of each of the first two WORDS, so a two-word display name came
// out as "JC" on the assignee avatars while every other avatar in the app showed
// "JO" — the shared `avatarInitials` (first two chars of the display name) is the
// single source of truth. Don't reintroduce a second initials rule here.)

const isOverdue = (iso) => {
    if (!iso) return false;
    // #248: same naive-UTC parse as the chip above — comparing a naive due date as
    // if it were local time flagged a ticket overdue hours early (UTC+7) or late (UTC-5).
    try { const d = parseServerDate(iso); return !!d && d < new Date(); } catch { return false; }
};

// ─────────────────────────────────────────────────────────────────────────
// In-ticket attachment preview (auth'd blob URL — same pattern as MessageAttachment).
// The thumbnail endpoint renders images as-is and PDFs as their first page, so this
// previews both (Kanban Ticket Document Attachments #92).
// ─────────────────────────────────────────────────────────────────────────

// (Ticket attachments Boards #108 removed the inline AttachmentPreview thumbnails — the
// attachments list is now filename-only, with click-to-preview via DocumentPreviewModal.)

// ─────────────────────────────────────────────────────────────────────────
// Boards grid (no board selected)
// ─────────────────────────────────────────────────────────────────────────

const BoardCard = ({ board, onOpen }) => {
    const { t } = useLanguage();
    return (
    <button
        data-testid="board-card"
        onClick={() => onOpen(board.id)}
        // PAI board card: aspect 0.94, 16px radius, .12 hairline over a .03 pane; hover
        // lifts the wash to .08 and the border to .30. overflow-hidden keeps a long
        // description inside the fixed footprint so the stats line can never be pushed
        // out of the card (#142 asserts the metadata stays within its card box).
        className="min-w-0 overflow-hidden text-left aspect-[0.94] rounded-card border border-white/[0.12] bg-white/[0.03] p-5 flex flex-col gap-2 transition-colors duration-150 hover:bg-white/[0.08] hover:border-white/[0.3]"
    >
        <div className="flex items-start justify-between gap-2">
            <h3 className="min-w-0 text-[18px] font-bold tracking-[-0.015em] text-white truncate">
                    {board.name}
                </h3>
            {board.viewer_role === 'owner' && (
                <span className="flex-shrink-0 rounded-[7px] border border-white/[0.18] px-2.5 py-[5px] text-[10.5px] font-bold uppercase tracking-[0.07em] text-white/75">
                    {t('teamRoleOwner', 'Owner')}
                </span>
            )}
        </div>
        {board.description && (
            <p className="text-[13.5px] text-pai-meta line-clamp-2 break-words">
                {board.description}
            </p>
        )}
        {/* flex-wrap so the metadata never breaks out of the card at narrow widths
            (Adjusting UI Boards screen space #142) — it stacks onto a second line instead. */}
        <div data-testid="board-metadata" className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13.5px] text-pai-meta">
            <span className="tnum">{t('brdColumnsCount', '{n} columns', { n: board.column_count ?? 0 })}</span>
            <span className="text-white/25">·</span>
            <span className="tnum">{t('brdTicketsCount', '{n} tickets', { n: board.ticket_count ?? 0 })}</span>
            {(board.member_count ?? 0) > 1 && (
                <>
                    <span className="text-white/25">·</span>
                    <span className="flex items-center gap-1.5">
                        <Users size={13} />
                        <span className="tnum">{board.member_count}</span>
                    </span>
                </>
            )}
        </div>
    </button>
    );
};

const NewBoardCard = ({ onCreate }) => {
    const { t } = useLanguage();
    return (
    <button
        data-testid="new-board-card"
        onClick={onCreate}
        // Same footprint as a board card, drawn as the dashed "add" variant.
        className="min-w-0 overflow-hidden aspect-[0.94] rounded-card border border-dashed border-white/20 bg-transparent p-5 flex flex-col items-center justify-center gap-4 text-white/60 transition-colors duration-150 hover:border-white/[0.45] hover:bg-white/[0.03] hover:text-white"
    >
        <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-white/[0.28]">
            <Plus size={26} strokeWidth={1.25} />
        </span>
        <span className="text-[15px] font-medium">{t('brdNewBoard', 'New board')}</span>
    </button>
    );
};

// Cleanup Boards #496: name only — the old accent-colour palette was never applied to
// the board card or header, so it was removed (and the request no longer sends `color`).
const NewBoardDialog = ({ onClose, onCreate }) => {
    const { t } = useLanguage();
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim() || busy) return;
        setBusy(true);
        try { await onCreate({ name: name.trim() }); }
        finally { setBusy(false); }
    };
    return (
        // No backdrop dismiss: a stray click must not drop a half-typed board.
        <Modal onClose={onClose} closeOnBackdrop={false} cardClassName="p-7">
            <form onSubmit={submit}>
                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-[22px] font-bold tracking-[-0.02em] text-white">{t('brdNewBoard', 'New board')}</h2>
                    <button type="button" onClick={onClose} className="pai-icon-btn w-[38px] h-[38px] -mr-2"><X size={18} /></button>
                </div>
                <label className="pai-eyebrow block mb-2">{t('commonName', 'Name')}</label>
                <input
                    autoFocus
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('brdBoardNamePlaceholder', 'e.g. Apartment move, Q3 roadmap')}
                    className="pai-input w-full h-11 px-3.5 text-[15px]"
                />
                <div className="mt-7 flex justify-end gap-2.5">
                    <button type="button" onClick={onClose} className="pai-btn-ghost">{t('commonCancel', 'Cancel')}</button>
                    <button type="submit" disabled={!name.trim() || busy} className="pai-btn-primary disabled:opacity-50">
                        {busy ? t('brdCreatingBoard', 'Creating...') : t('brdCreateBoard', 'Create board')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

// Adjusting UI Boards screen space #142: the board LIST must stay readable at ANY
// width. Tailwind's `sm:`/`lg:` breakpoints key off the VIEWPORT, not the panel — so a
// narrow Boards panel docked in a wide viewport got `lg:grid-cols-3` and crushed 3 cards
// into ~120px each (metadata broke out, overlapped the title / New Board button). Use a
// CONTAINER-driven auto-fill grid instead (inline style so there's no Tailwind
// arbitrary-value parsing surprise): each card is minmax(min(200px,100%),1fr) — at least
// ~200px, never wider than the container, columns adapt to the real panel width.
const BoardsGrid = ({ boards, onOpen, onCreate }) => (
    <div
        data-testid="boards-grid"
        // items-start: a grid item defaults to align-self:stretch, which would size the
        // card to the row and defeat its aspect-[0.94]. content-start keeps the rows
        // packed at the top (the handoff's `align-content: start`).
        className="px-7 pb-7 grid gap-5 content-start items-start"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(200px, 100%), 1fr))' }}
    >
        {boards.map((b) => (
            <BoardCard key={b.id} board={b} onOpen={onOpen} />
        ))}
        <NewBoardCard onCreate={onCreate} />
    </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Ticket card (in a column)
// ─────────────────────────────────────────────────────────────────────────

// PAI Tick #213 — the per-card completion tick. Empty circle outline that only
// reveals on card hover (group-hover) while incomplete; a filled dark-green circle
// with a white check, always visible, once completed. Clicking toggles is_completed.
const TicketTick = ({ ticket, completed, pending, onToggleComplete }) => {
    const { t } = useLanguage();
    return (
    <button
        type="button"
        data-testid="ticket-tick"
        data-completed={completed ? 'true' : 'false'}
        disabled={pending}
        aria-pressed={completed}
        aria-label={completed ? t('brdMarkNotCompleted', 'Mark ticket not completed') : t('brdMarkCompleted', 'Mark ticket completed')}
        title={completed ? t('brdCompletedClickUndo', 'Completed — click to mark not done') : t('brdMarkAsCompleted', 'Mark as completed')}
        draggable={false}
        onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); if (!pending) onToggleComplete?.(ticket); }}
        className={`flex-shrink-0 flex items-center justify-center w-4 h-4 rounded-full transition-opacity ${
            completed ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 pai-touch-reveal'
        }`}
    >
        {completed ? (
            // PAI accent rule: green means "completed". The filled circle-check is the
            // handoff's own completed marker (a small icon fill, not a panel).
            <span className="flex items-center justify-center w-4 h-4 rounded-full bg-pai-green text-white">
                <Check size={11} strokeWidth={3} />
            </span>
        ) : (
            <Circle size={16} className="text-white/45" />
        )}
    </button>
    );
};

const TicketCard = ({ ticket, onOpen, onDragStart, onDragEnd, dragging, onReorderDrop, onMove, onToggleComplete }) => {
    const { t } = useLanguage();
    const overdue = isOverdue(ticket.due_date) && !ticket.is_archived;
    const pending = !!ticket._pending;
    const completed = !!ticket.is_completed;  // PAI Tick #213

    // Manual Ticket movement #136: a kebab menu at the card's top-right corner (cards have
    // no "X" button, so the spec's "left of the X / top-right corner" resolves to top-right).
    // The menu is PORTALED to <body> so the column's overflow-y-auto scroller can't clip it.
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const menuBtnRef = useRef(null);
    const popoverRef = useRef(null);

    const openMenu = (e) => {
        e.stopPropagation();
        const r = menuBtnRef.current?.getBoundingClientRect();
        if (!r) return;
        const openUp = (window.innerHeight - r.bottom) < 120;
        setMenuPos({
            right: Math.max(8, window.innerWidth - r.right),
            top: openUp ? undefined : r.bottom + 4,
            bottom: openUp ? (window.innerHeight - r.top + 4) : undefined,
        });
        setMenuOpen(true);
    };

    useEffect(() => {
        if (!menuOpen) return;
        const onDown = (e) => {
            if (popoverRef.current?.contains(e.target)) return;
            if (menuBtnRef.current?.contains(e.target)) return;
            setMenuOpen(false);
        };
        const onDismiss = () => setMenuOpen(false);
        window.addEventListener('mousedown', onDown);
        window.addEventListener('scroll', onDismiss, true);
        window.addEventListener('resize', onDismiss);
        return () => {
            window.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', onDismiss, true);
            window.removeEventListener('resize', onDismiss);
        };
    }, [menuOpen]);
    // Live vertical displacement (Ui movement Board #95): while another ticket is dragged
    // over this one, open an animated gap ABOVE ('before') or BELOW ('after') depending on
    // the cursor's position relative to THIS card's midpoint, previewing the landing slot.
    // The drag target is the STABLE wrapper (not the card) and the gap is an inner spacer,
    // so growing the gap never moves the hitbox out from under the cursor → no jitter. The
    // before/after split is what makes single-step moves register (the old code always
    // inserted "before", so moving one position down was a no-op).
    const [dropPos, setDropPos] = useState(null); // null | 'before' | 'after'
    const cardRef = useRef(null);

    const computePos = (e) => {
        const el = cardRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const next = e.clientY > rect.top + rect.height / 2 ? 'after' : 'before';
        setDropPos((p) => (p === next ? p : next));
    };

    const gapClass = (active) =>
        `transition-all duration-150 overflow-hidden rounded-row ${
            active ? 'h-12 border border-dashed border-white/30 bg-white/[0.06]' : 'h-0'
        }`;

    return (
        <div
            data-testid="board-ticket-slot"
            onDragOver={(e) => {
                if (!e.dataTransfer.types.includes('application/x-omnis-ticket')) return;
                e.preventDefault();
                e.stopPropagation();
                if (dragging) return; // don't open a gap on the card being dragged
                computePos(e);
            }}
            onDragLeave={(e) => {
                // Only clear when the cursor truly leaves this wrapper — moving between the
                // inner card and its spacers must NOT reset (that's what caused jitter).
                if (e.currentTarget.contains(e.relatedTarget)) return;
                setDropPos(null);
            }}
            onDrop={(e) => {
                if (!e.dataTransfer.types.includes('application/x-omnis-ticket')) return;
                e.preventDefault();
                e.stopPropagation();   // don't also trigger the column's append-drop
                const pos = dropPos || 'before';
                setDropPos(null);
                try {
                    const payload = JSON.parse(e.dataTransfer.getData('application/x-omnis-ticket'));
                    if (payload.ticket_id && payload.ticket_id !== ticket.id) {
                        onReorderDrop?.(payload.ticket_id, ticket.id, pos);
                    }
                } catch { /* ignore */ }
            }}
        >
            {/* gap shown above when landing 'before' this card */}
            <div aria-hidden data-testid="ticket-drop-gap-before" className={`${gapClass(dropPos === 'before')} ${dropPos === 'before' ? 'mb-2' : ''}`} />
            <div
                ref={cardRef}
                data-testid="board-ticket"
                data-ticket-id={ticket.id}
                draggable={!pending}
                onDragStart={(e) => {
                    if (pending) { e.preventDefault(); return; }
                    e.dataTransfer.setData('application/x-omnis-ticket',
                        JSON.stringify({ ticket_id: ticket.id, column_id: ticket.column_id }));
                    e.dataTransfer.effectAllowed = 'move';
                    onDragStart?.(ticket);
                }}
                onDragEnd={() => { setDropPos(null); onDragEnd?.(ticket); }}
                onClick={() => { if (!pending) onOpen?.(ticket); }}
                data-completed={completed ? 'true' : 'false'}
                // PAI ticket card: 12px radius over a .04 wash with a .10 hairline; hover
                // deepens to .09 / .26. The COMPLETED variant recedes (.02 / .07) — and
                // keeps its slight dim, which PAI Tick #213 asserts (card opacity < 1
                // while completed, back above 0.99 once un-ticked).
                className={`group relative rounded-row border ${
                    completed ? 'border-white/[0.07] bg-white/[0.02]' : 'border-white/[0.1] bg-white/[0.04]'
                } ${
                    pending ? 'opacity-60 cursor-progress' : 'cursor-pointer hover:bg-white/[0.09] hover:border-white/[0.26]'
                } transition-all ${
                    dragging ? 'opacity-40' : completed ? 'opacity-70' : ''
                }`}
            >
            {/* Manual Ticket movement #136: three-dot menu at the top-right corner.
                stopPropagation so tapping it doesn't open the ticket detail. */}
            {!pending && (
                <button
                    ref={menuBtnRef}
                    data-testid="ticket-card-menu"
                    aria-label={t('brdTicketOptions', 'Ticket options')}
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    onClick={openMenu}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="pai-tap touch:flex touch:items-center touch:justify-center absolute top-1.5 right-1.5 touch:top-0 touch:right-0 z-10 p-1 rounded-chip text-white/45 hover:text-white hover:bg-white/[0.09] transition-colors"
                >
                    <MoreHorizontal size={14} />
                </button>
            )}
            {menuOpen && menuPos && createPortal(
                <div
                    ref={popoverRef}
                    role="menu"
                    data-testid="ticket-card-menu-popover"
                    onClick={(e) => e.stopPropagation()}
                    style={{ position: 'fixed', top: menuPos.top, bottom: menuPos.bottom, right: menuPos.right }}
                    className="z-[90] w-40 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5"
                >
                    <button
                        data-testid="ticket-menu-move"
                        role="menuitem"
                        onClick={(e) => { e.stopPropagation(); setMenuOpen(false); onMove?.(ticket); }}
                        className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13.5px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white transition-colors"
                    >
                        <MoveRight size={14} /> {t('brdMove', 'Move')}
                    </button>
                </div>,
                document.body,
            )}
            {/* Board cards stay clean — name + labels only, no covers/thumbnails
                (Kanban Ticket Document Attachments #92). Attachments + previews live
                inside the ticket detail. */}
            <div className="p-3.5 pr-7 touch:pr-11 space-y-2.5">
                {/* PAI Tick #213: a completion tick. No labels → top-left of the card;
                    labels present → immediately AFTER the last label chip (same row, so
                    it scales with any number of labels). The empty outline only shows on
                    card hover (group-hover); once completed it's a filled dark-green
                    circle + white check that's always visible. Kept out of the drag path
                    (stopPropagation on mousedown, non-draggable) so it never steals the
                    card's drag handle. */}
                {ticket.labels && ticket.labels.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-[7px]">
                        {ticket.labels.map((l) => (
                            // Label chips stay FILLED — one of the three documented exceptions
                            // to the accent rule. The hex is the BE-mirrored #289 contract;
                            // only the ink is derived (see labelInkFor).
                            <span
                                key={l.id}
                                className="text-[11.5px] font-bold leading-none px-2.5 py-[5px] rounded-chip"
                                style={{ background: l.color || '#6366f1', color: labelInkFor(l.color || '#6366f1') }}
                            >
                                {l.name}
                            </span>
                        ))}
                        <TicketTick ticket={ticket} completed={completed} pending={pending} onToggleComplete={onToggleComplete} />
                    </div>
                ) : (
                    <div className="flex items-center">
                        <TicketTick ticket={ticket} completed={completed} pending={pending} onToggleComplete={onToggleComplete} />
                    </div>
                )}
                {/* #432: an unbreakable token (an id, a URL) in a title used to
                    paint 600px past the card; it now wraps inside it. */}
                <div className={`text-[15px] font-semibold leading-[1.35] break-words [overflow-wrap:anywhere] ${
                    completed ? 'line-through text-white/45' : 'text-white'
                }`}>
                    {ticket.title}
                </div>
                {(ticket.due_date || ticket.comment_count > 0 || ticket.attachment_count > 0 || (ticket.assignees?.length || 0) > 0) && (
                    <div className="flex items-center gap-2.5 text-[12.5px] text-pai-meta">
                        {ticket.due_date && (
                            <span className={`flex items-center gap-1.5 tnum ${overdue ? 'text-pai-red font-semibold' : ''}`}>
                                <CalendarIcon size={13} />
                                {formatDateChip(ticket.due_date)}
                            </span>
                        )}
                        {ticket.comment_count > 0 && (
                            <span data-testid="card-comment-count" className="flex items-center gap-1 tnum">
                                <MessageSquare size={13} />
                                {ticket.comment_count}
                            </span>
                        )}
                        {ticket.attachment_count > 0 && (
                            <span data-testid="card-attachment-count" className="flex items-center gap-1 tnum">
                                <Paperclip size={13} />
                                {ticket.attachment_count}
                            </span>
                        )}
                        {ticket.assignees && ticket.assignees.length > 0 && (
                            <span className="ml-auto flex -space-x-1">
                                {/* Board UI consistency (#258): the global avatar style —
                                    white background / black initials, from the shared
                                    avatarInitials (first two chars of the display name).
                                    These used to be purple gradient balls labelled by a
                                    local first-letters-of-words helper, so Jorge read
                                    "JC" here and "JO" everywhere else. */}
                                {ticket.assignees.slice(0, 3).map((a) => (
                                    <span
                                        key={a.user_id}
                                        data-testid="assignee-avatar"
                                        title={a.email || a.username}
                                        className="w-5 h-5 rounded-full bg-white text-pai-void text-[9px] flex items-center justify-center font-bold ring-2 ring-pai-base select-none"
                                    >
                                        {avatarInitials(a)}
                                    </span>
                                ))}
                            </span>
                        )}
                    </div>
                )}
            </div>
            </div>
            {/* gap shown below when landing 'after' this card */}
            <div aria-hidden data-testid="ticket-drop-gap-after" className={`${gapClass(dropPos === 'after')} ${dropPos === 'after' ? 'mt-2' : ''}`} />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────
// Column (header + ticket list, drop target)
// ─────────────────────────────────────────────────────────────────────────

const ColumnHeader = ({ column, onRename, onDelete, onMove, ticketCount, onColumnDragStart, onColumnDragEnd, columnRef }) => {
    const { t } = useLanguage();
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(column.name);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;
        const handle = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [menuOpen]);

    const save = async () => {
        if (name.trim() && name !== column.name) await onRename(column.id, name.trim());
        setEditing(false);
    };

    return (
        // Moving columns Boards #103: the header is the column's drag handle (grab to
        // reorder horizontally). draggable is off while renaming so the input works.
        <div
            data-testid="column-header"
            data-column-id={column.id}
            draggable={!editing}
            onDragStart={(e) => {
                if (editing) { e.preventDefault(); return; }
                e.dataTransfer.setData('application/x-omnis-column', JSON.stringify({ column_id: column.id }));
                e.dataTransfer.effectAllowed = 'move';
                // Ui fluidity #138: drag the WHOLE column (header + tickets) as one drag
                // image, not just the header — so the entire column visibly travels with the
                // cursor. The source column is dimmed via the `dragging` flag (below) so the
                // displacement of the neighbours previews the new layout live.
                const colEl = columnRef?.current;
                if (colEl && typeof e.dataTransfer.setDragImage === 'function') {
                    try {
                        const r = colEl.getBoundingClientRect();
                        e.dataTransfer.setDragImage(colEl, e.clientX - r.left, e.clientY - r.top);
                    } catch { /* setDragImage unsupported (older browsers) — degrade gracefully */ }
                }
                onColumnDragStart?.(column);
            }}
            onDragEnd={() => onColumnDragEnd?.(column)}
            className={`flex-shrink-0 flex items-center justify-between gap-2 mb-3 ${editing ? '' : 'cursor-grab active:cursor-grabbing'}`}
        >
            {editing ? (
                <input
                    autoFocus
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={save}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') save();
                        if (e.key === 'Escape') { setName(column.name); setEditing(false); }
                    }}
                    className="pai-input flex-1 min-w-0 px-2 py-1 text-[16px] font-bold"
                />
            ) : (
                <button
                    onDoubleClick={() => setEditing(true)}
                    className="pai-tap-row flex-1 min-w-0 text-left text-[16px] font-bold text-white truncate flex items-center gap-2"
                >
                    <GripVertical size={15} className="text-white opacity-35 flex-shrink-0" />
                    <span className="truncate">{column.name}</span>
                    <span className="ml-0.5 text-[14px] font-medium text-white/45 tnum flex-shrink-0">
                        {ticketCount}
                    </span>
                </button>
            )}
            <div className="relative flex-shrink-0" ref={menuRef}>
                <button
                    onClick={() => setMenuOpen((v) => !v)}
                    data-testid="column-menu-button"
                    aria-label={t('brdColumnMenu', 'Column menu')}
                    className="pai-tap touch:flex touch:items-center touch:justify-center p-1 rounded-chip text-white/45 hover:text-white hover:bg-white/[0.07] transition-colors"
                >
                    <MoreHorizontal size={16} />
                </button>
                {menuOpen && (
                    <div className="absolute right-0 top-8 z-20 w-40 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5">
                        <button
                            onClick={() => { setMenuOpen(false); setEditing(true); }}
                            className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white flex items-center gap-2.5 transition-colors"
                        >
                            <Edit2 size={13} /> {t('commonRename', 'Rename')}
                        </button>
                        <button
                            data-testid="column-menu-move"
                            onClick={() => { setMenuOpen(false); onMove?.(column); }}
                            className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white flex items-center gap-2.5 transition-colors"
                        >
                            <ChevronRight size={13} /> {t('brdMove', 'Move')}
                        </button>
                        <button
                            onClick={() => { setMenuOpen(false); onDelete(column.id); }}
                            className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-red hover:bg-pai-red/[0.14] flex items-center gap-2.5 transition-colors"
                        >
                            <Trash2 size={13} /> {t('brdDeleteColumnMenuItem', 'Delete column')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const Column = ({
    column, tickets, onAddTicket, onOpenTicket, onRenameColumn, onDeleteColumn, onMoveColumn,
    onTicketDrop, onReorderDrop, onMoveTicket, onToggleComplete, draggingTicketId, onTicketDragStart, onTicketDragEnd,
    onColumnDragStart, onColumnDragEnd, dragging,
}) => {
    const { t } = useLanguage();
    const [dragOver, setDragOver] = useState(false);
    const [composing, setComposing] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    // Ui fluidity #138: ref to the whole column so ColumnHeader's onDragStart can set the
    // drag image to the ENTIRE column (header + tickets), not just the grabbed header.
    const columnRef = useRef(null);

    // Fire-and-forget submit: clear the input + keep the composer open so the
    // user can rapid-fire several tickets in a row (Trello-style). The
    // parent's optimistic update means the new ticket appears instantly even
    // before the API responds, so there's no need to disable / block.
    const submitNewTicket = (e) => {
        e?.preventDefault?.();
        const title = newTitle.trim();
        if (!title) { setComposing(false); return; }
        onAddTicket(column.id, title);
        setNewTitle('');
    };

    return (
        // Responsive scrollable column (Adjusting frame Boards #135): the column is
        // capped to the Kanban scroller's height (h-full / max-h-full — never taller
        // than the viewport), the header + Add-ticket stay pinned (flex-shrink-0) and
        // the ticket list (flex-1 + overflow-y-auto, min-h-0) scrolls INTERNALLY. The
        // parent slot chain (ColumnReorderSlot h-full → colRef h-full) propagates the
        // definite height so max-h-full resolves. Fixes the old bug where a tall column
        // overflowed past the scroller (overflow-y-hidden) and clipped the lower tickets
        // + the Add-ticket button below the browser frame.
        <div
            ref={columnRef}
            data-testid="board-column"
            data-dragging={dragging ? 'true' : 'false'}
            className={`w-[300px] flex-shrink-0 h-full max-h-full min-h-0 flex flex-col rounded-card border border-pai-subtle bg-white/[0.025] p-4 transition-opacity ${
                dragging ? 'opacity-40' : ''
            } ${
                dragOver ? 'ring-2 ring-white/40' : ''
            }`}
            onDragOver={(e) => {
                if (e.dataTransfer.types.includes('application/x-omnis-ticket')) {
                    e.preventDefault();
                    setDragOver(true);
                }
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
                setDragOver(false);
                try {
                    const payload = JSON.parse(e.dataTransfer.getData('application/x-omnis-ticket'));
                    onTicketDrop?.(payload.ticket_id, column.id);
                } catch { /* ignore */ }
            }}
        >
            <ColumnHeader
                column={column}
                columnRef={columnRef}
                onRename={onRenameColumn}
                onDelete={onDeleteColumn}
                onMove={onMoveColumn}
                ticketCount={tickets.length}
                onColumnDragStart={onColumnDragStart}
                onColumnDragEnd={onColumnDragEnd}
            />
            <div data-testid="column-ticket-list" className="flex-1 min-h-0 space-y-[11px] overflow-y-auto">
                {tickets.map((t) => (
                    <TicketCard
                        key={t.id}
                        ticket={t}
                        onOpen={onOpenTicket}
                        onReorderDrop={onReorderDrop}
                        onMove={onMoveTicket}
                        onToggleComplete={onToggleComplete}
                        onDragStart={onTicketDragStart}
                        onDragEnd={onTicketDragEnd}
                        dragging={draggingTicketId === t.id}
                    />
                ))}
            </div>
            {composing ? (
                <form onSubmit={submitNewTicket} className="flex-shrink-0 mt-3">
                    <textarea
                        autoFocus
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        onBlur={() => { if (!newTitle.trim()) setComposing(false); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitNewTicket(); }
                            if (e.key === 'Escape') { setNewTitle(''); setComposing(false); }
                        }}
                        rows={2}
                        placeholder={t('brdTicketTitlePlaceholder', 'Ticket title...')}
                        className="pai-input w-full px-3 py-2 text-[15px] resize-none"
                    />
                    <div className="mt-2 flex justify-end gap-2">
                        <button type="button" onClick={() => { setNewTitle(''); setComposing(false); }} className="px-2.5 py-1 text-[13px] font-medium text-pai-secondary hover:text-white hover:bg-white/[0.07] rounded-seg transition-colors">{t('commonCancel', 'Cancel')}</button>
                        <button type="submit" disabled={!newTitle.trim()} className="px-3 py-1 text-[13px] font-semibold bg-white text-pai-void rounded-seg hover:bg-white/[0.88] disabled:opacity-50 transition-colors">{t('commonAdd', 'Add')}</button>
                    </div>
                </form>
            ) : (
                <button
                    onClick={() => setComposing(true)}
                    data-testid="column-add-ticket"
                    className="flex-shrink-0 mt-3 flex items-center gap-1.5 px-1 py-1.5 text-[14px] font-semibold text-white/50 hover:text-white rounded-seg transition-colors"
                >
                    <Plus size={17} /> {t('brdAddTicket', 'Add ticket')}
                </button>
            )}
        </div>
    );
};

// Horizontal column displacement slot (Moving columns Boards #103). Wraps a Column and,
// while ANOTHER column is dragged over it, opens an animated gap to the LEFT ('before') or
// RIGHT ('after') based on the cursor vs this column's horizontal midpoint — the column
// analog of TicketCard's vertical gap. The slot is the stable drop target and the gap is a
// sibling spacer, so growing it never moves the column out from under the cursor (no jitter).
const ColumnReorderSlot = ({ columnId, draggingColumnId, onReorder, children }) => {
    const [pos, setPos] = useState(null); // null | 'before' | 'after'
    const colRef = useRef(null);
    const isDragging = draggingColumnId === columnId;

    const gapClass = (active) =>
        `transition-all duration-150 self-stretch rounded-chip ${
            active ? 'w-2 mx-1 bg-white/25' : 'w-0'
        }`;

    return (
        <div
            data-testid="column-slot"
            // h-full: propagate the scroller's definite height down to the Column so its
            // max-h-full caps to the viewport and the ticket list scrolls (Adjusting frame #135).
            className="flex items-stretch h-full"
            onDragOver={(e) => {
                if (!e.dataTransfer.types.includes('application/x-omnis-column')) return;
                e.preventDefault();
                if (isDragging) return;
                const r = colRef.current?.getBoundingClientRect();
                if (!r) return;
                const next = e.clientX > r.left + r.width / 2 ? 'after' : 'before';
                setPos((p) => (p === next ? p : next));
            }}
            onDragLeave={(e) => { if (e.currentTarget.contains(e.relatedTarget)) return; setPos(null); }}
            onDrop={(e) => {
                if (!e.dataTransfer.types.includes('application/x-omnis-column')) return;
                e.preventDefault();
                const p = pos || 'before';
                setPos(null);
                try {
                    const payload = JSON.parse(e.dataTransfer.getData('application/x-omnis-column'));
                    if (payload.column_id && payload.column_id !== columnId) onReorder(payload.column_id, columnId, p);
                } catch { /* ignore */ }
            }}
        >
            <div aria-hidden data-testid="column-drop-gap-before" className={gapClass(pos === 'before')} />
            <div ref={colRef} className="h-full min-h-0">{children}</div>
            <div aria-hidden data-testid="column-drop-gap-after" className={gapClass(pos === 'after')} />
        </div>
    );
};

// Move a whole column (with its tickets) to a different board (Manual column move #110).
// Pick a destination board + an insert position (1..destColumns+1); on confirm the column
// and all its tickets are transferred server-side.
const ColumnMoveDialog = ({ column, currentBoardId, onClose, onMoved }) => {
    const { t } = useLanguage();
    const [boards, setBoards] = useState([]);
    const [targetBoardId, setTargetBoardId] = useState('');
    const [position, setPosition] = useState(1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await boardService.listBoards();
                const others = (resp.boards || []).filter((b) => b.id !== currentBoardId);
                if (!cancelled) setBoards(others);
            } catch (e) {
                if (!cancelled) setError(t('brdFailedLoadBoards', 'Failed to load boards.'));
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above: 't' is intentionally omitted
    }, [currentBoardId]);

    const target = boards.find((b) => b.id === targetBoardId);
    const maxPos = (target?.column_count ?? 0) + 1;
    const positions = Array.from({ length: maxPos }, (_, i) => i + 1);

    const submit = async () => {
        if (!targetBoardId || busy) return;
        setBusy(true);
        setError('');
        try {
            await boardService.moveColumn(column.id, targetBoardId, position - 1);
            onMoved?.();
        } catch (e) {
            setError(userErrorMessage(e, t));
            setBusy(false);
        }
    };

    const [moveDescPre, moveDescPost] = tSplit(t, 'brdMoveColumnDesc', 'Move "{name}" and all its tickets to another board.', 'name');

    return (
        <Modal onClose={onClose} size="sm" closeOnBackdrop={false} scrimTestId="column-move-dialog" cardClassName="p-7">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-[22px] font-bold tracking-[-0.02em] text-white">{t('columnMoveTitle', 'Move column')}</h2>
                    <button onClick={onClose} aria-label={t('commonClose', 'Close')} className="pai-icon-btn w-[38px] h-[38px] -mr-2"><X size={18} /></button>
                </div>
                <p className="text-[13.5px] text-pai-meta mb-5">{moveDescPre}<span className="font-semibold text-white">{column.name}</span>{moveDescPost}</p>

                <label className="pai-eyebrow block mb-2">{t('brdDestinationBoard', 'Destination board')}</label>
                <select
                    data-testid="column-move-board"
                    value={targetBoardId}
                    onChange={(e) => { setTargetBoardId(e.target.value); setPosition(1); }}
                    className="pai-input w-full mb-4 h-10 px-3.5 text-[14px] bg-pai-modal"
                >
                    <option value="">{t('brdSelectABoard', 'Select a board…')}</option>
                    {boards.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                </select>

                <label className="pai-eyebrow block mb-2">{t('brdPosition', 'Position')}</label>
                <select
                    data-testid="column-move-position"
                    value={position}
                    onChange={(e) => setPosition(Number(e.target.value))}
                    disabled={!targetBoardId}
                    className="pai-input w-full mb-5 h-10 px-3.5 text-[14px] tnum bg-pai-modal disabled:opacity-50"
                >
                    {positions.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>

                {error && <div className="mb-3 text-[13px] text-pai-red">{error}</div>}
                <div className="flex justify-end gap-2.5">
                    <button onClick={onClose} className="pai-btn-ghost">{t('commonCancel', 'Cancel')}</button>
                    <button
                        data-testid="column-move-confirm"
                        onClick={submit}
                        disabled={!targetBoardId || busy}
                        className="pai-btn-primary disabled:opacity-50"
                    >
                        {busy ? t('columnMoveBusy', 'Moving…') : t('columnMoveTitle', 'Move column')}
                    </button>
                </div>
            </Modal>
    );
};

// Manual Ticket movement #136: move a single ticket to any board / column / position via a
// three-step selection flow (Board → Column → Position). Positions reflect the destination
// column's CURRENT ticket count in real time: N+1 slots when moving INTO a different column,
// N slots when re-slotting within the ticket's own column (the ticket itself is one of them).
const TicketMoveDialog = ({ ticket, currentBoardId, onClose, onMoved }) => {
    const { t } = useLanguage();
    const [boards, setBoards] = useState([]);
    const [targetBoardId, setTargetBoardId] = useState(currentBoardId || '');
    const [columns, setColumns] = useState([]);
    const [columnsLoading, setColumnsLoading] = useState(false);
    const [selectedColumnId, setSelectedColumnId] = useState('');
    const [position, setPosition] = useState(1);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Step 1 — load every board the user can move into.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const resp = await boardService.listBoards();
                if (!cancelled) setBoards(resp.boards || []);
            } catch (e) {
                if (!cancelled) setError(t('brdFailedLoadBoards', 'Failed to load boards.'));
            }
        })();
        return () => { cancelled = true; };
    }, [t]);

    // Step 2 — load the chosen board's columns (with live ticket counts) whenever it changes.
    useEffect(() => {
        if (!targetBoardId) { setColumns([]); setSelectedColumnId(''); return; }
        let cancelled = false;
        setColumnsLoading(true);
        (async () => {
            try {
                const full = await boardService.getBoardFull(targetBoardId);
                if (cancelled) return;
                const cols = full.columns || [];
                setColumns(cols);
                const stayOnBoard = targetBoardId === currentBoardId
                    && cols.some((c) => c.id === ticket.column_id);
                setSelectedColumnId(stayOnBoard ? ticket.column_id : (cols[0]?.id || ''));
                setPosition(1);
            } catch (e) {
                if (!cancelled) { setColumns([]); setError(t('brdFailedLoadColumns', 'Failed to load columns.')); }
            } finally {
                if (!cancelled) setColumnsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see note above: 't' is intentionally omitted
    }, [targetBoardId, currentBoardId, ticket.column_id]);

    const selectedColumn = columns.find((c) => c.id === selectedColumnId);
    const isCurrentColumn = targetBoardId === currentBoardId && selectedColumnId === ticket.column_id;
    const count = selectedColumn ? (selectedColumn.tickets?.length ?? selectedColumn.ticket_count ?? 0) : 0;
    // N slots when re-slotting within the ticket's own column; N+1 when landing in another.
    const maxPos = Math.max(1, isCurrentColumn ? count : count + 1);
    const positions = Array.from({ length: maxPos }, (_, i) => i + 1);

    const submit = async () => {
        if (!targetBoardId || !selectedColumnId || busy) return;
        setBusy(true);
        setError('');
        try {
            // position is 1-based in the UI, 0-based on the wire.
            await boardService.moveTicket(ticket.id, selectedColumnId, position - 1);
            onMoved?.({ targetBoardId, sourceBoardId: currentBoardId });
        } catch (e) {
            setError(userErrorMessage(e, t));
            setBusy(false);
        }
    };

    const [movingDescPre, movingDescPost] = tSplit(t, 'brdMovingTicketDesc', 'Moving "{title}"', 'title');

    return (
        <Modal onClose={onClose} zIndex="z-[70]" size="sm" closeOnBackdrop={false} scrimTestId="ticket-move-dialog" cardClassName="p-7">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-[22px] font-bold tracking-[-0.02em] text-white">{t('brdMoveTicket', 'Move ticket')}</h2>
                    <button onClick={onClose} aria-label={t('commonClose', 'Close')} data-testid="ticket-move-close" className="pai-icon-btn w-[38px] h-[38px] -mr-2"><X size={18} /></button>
                </div>
                <p className="text-[13.5px] text-pai-meta mb-5 truncate">
                    {movingDescPre}<span className="font-semibold text-white">{ticket.title}</span>{movingDescPost}
                </p>

                <label className="pai-eyebrow block mb-2">{t('brdBoardLabel', 'Board')}</label>
                <select
                    data-testid="ticket-move-board"
                    value={targetBoardId}
                    onChange={(e) => setTargetBoardId(e.target.value)}
                    className="pai-input w-full mb-4 h-10 px-3.5 text-[14px] bg-pai-modal"
                >
                    <option value="">{t('brdSelectABoard', 'Select a board…')}</option>
                    {boards.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}{b.id === currentBoardId ? t('brdCurrentSuffix', ' (current)') : ''}</option>
                    ))}
                </select>

                <label className="pai-eyebrow block mb-2">{t('brdColumnLabel', 'Column')}</label>
                <select
                    data-testid="ticket-move-column"
                    value={selectedColumnId}
                    onChange={(e) => { setSelectedColumnId(e.target.value); setPosition(1); }}
                    disabled={!targetBoardId || columnsLoading || columns.length === 0}
                    className="pai-input w-full mb-4 h-10 px-3.5 text-[14px] bg-pai-modal disabled:opacity-50"
                >
                    {columnsLoading && <option value="">{t('brdLoadingEllipsis', 'Loading…')}</option>}
                    {!columnsLoading && columns.length === 0 && <option value="">{t('brdNoColumns', 'No columns')}</option>}
                    {!columnsLoading && columns.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>

                <label className="pai-eyebrow block mb-2">{t('brdPosition', 'Position')}</label>
                <select
                    data-testid="ticket-move-position"
                    value={position}
                    onChange={(e) => setPosition(Number(e.target.value))}
                    disabled={!selectedColumnId}
                    className="pai-input w-full mb-5 h-10 px-3.5 text-[14px] tnum bg-pai-modal disabled:opacity-50"
                >
                    {positions.map((p) => (<option key={p} value={p}>{p}</option>))}
                </select>

                {error && <div className="mb-3 text-[13px] text-pai-red">{error}</div>}
                <div className="flex justify-end gap-2.5">
                    <button onClick={onClose} className="pai-btn-ghost">{t('commonCancel', 'Cancel')}</button>
                    <button
                        data-testid="ticket-move-confirm"
                        onClick={submit}
                        disabled={!targetBoardId || !selectedColumnId || busy}
                        className="pai-btn-primary disabled:opacity-50"
                    >
                        {busy ? t('columnMoveBusy', 'Moving…') : t('brdMoveTicket', 'Move ticket')}
                    </button>
                </div>
            </Modal>
    );
};

// "Select from File Manager" cloud-picker (Document attachment Boards #117): the
// searchable existing-document picker now lives in the shared `AttachPickerModal`
// component (reused by the chat composer's "+" menu, #208). Imported above.

// ─────────────────────────────────────────────────────────────────────────
// Ticket detail modal
// ─────────────────────────────────────────────────────────────────────────

// Label management dropdown (Board labels #85): toggle existing labels on/off for
// the ticket AND manage the board's label repository — create a new label (pick one
// of 8 colors + type a name), edit a label's name/color, or delete it (which removes
// it from every ticket). Edits reflect across all tickets because labels live on the
// board, not the ticket.
const LabelPicker = ({ labels, ticketLabelIds, onToggle, onCreate, onUpdate, onDelete }) => {
    const { t } = useLanguage();
    const [mode, setMode] = useState(null);      // null | 'create' | <labelId being edited>
    const [draftName, setDraftName] = useState('');
    const [draftColor, setDraftColor] = useState(DEFAULT_LABEL_COLOR);
    const [busy, setBusy] = useState(false);

    const startCreate = () => { setMode('create'); setDraftName(''); setDraftColor(DEFAULT_LABEL_COLOR); };
    const startEdit = (l) => { setMode(l.id); setDraftName(l.name || ''); setDraftColor(l.color || DEFAULT_LABEL_COLOR); };
    const cancel = () => { setMode(null); setDraftName(''); };

    const submit = async () => {
        const name = draftName.trim();
        if (!name || busy) return;
        setBusy(true);
        try {
            if (mode === 'create') {
                // Create & Add (Labels Board Update #113): create the label AND assign it
                // to the current ticket in one click.
                const created = await onCreate(name, draftColor);
                if (created?.id) await onToggle(created.id);
            } else {
                await onUpdate(mode, { name, color: draftColor });
            }
            cancel();
        } catch (err) {
            alert(prefixedUserErrorMessage(t('brdFailedSaveLabel', 'Failed to save label'), err));
        } finally { setBusy(false); }
    };

    const remove = async (labelId) => {
        if (busy) return;
        if (!confirm(t('brdConfirmDeleteLabel', 'Delete this label? It will be removed from every ticket that uses it.'))) return;
        setBusy(true);
        try { await onDelete(labelId); }
        catch (err) { alert(prefixedUserErrorMessage(t('brdFailedDeleteLabel', 'Failed to delete label'), err)); }
        finally { setBusy(false); }
    };

    const form = (
        <div data-testid="label-form" className="rounded-ctl border border-pai-subtle bg-white/[0.03] p-2.5 space-y-2.5">
            <input
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); if (e.key === 'Escape') cancel(); }}
                placeholder={t('brdLabelNamePlaceholder', 'Label name (e.g. Phase 1)')}
                data-testid="label-name-input"
                // Labels Ui update #289 (PINNED): NO focus treatment at all — the box keeps
                // the exact same border when focused and must report boxShadow:none, so this
                // deliberately does NOT use `pai-input` (whose focus state is the glow).
                className="w-full px-2.5 py-1.5 text-[13px] rounded-seg border border-pai bg-white/[0.03] text-white placeholder:text-pai-placeholder focus:outline-none"
            />
            <div className="flex flex-wrap gap-2">
                {LABEL_COLORS.map((c) => (
                    <button
                        key={c}
                        type="button"
                        onClick={() => setDraftColor(c)}
                        data-testid="label-color-swatch"
                        aria-label={`${t('brdColorLabel', 'color')} ${c}`}
                        className={`w-6 h-6 rounded-full transition ${draftColor === c ? 'ring-2 ring-offset-1 ring-white ring-offset-pai-modal' : ''}`}
                        style={{ background: c }}
                    />
                ))}
            </div>
            <div className="flex gap-1 justify-end">
                <button type="button" onClick={cancel} className="px-2.5 py-1 text-[13px] font-medium text-pai-secondary hover:text-white hover:bg-white/[0.07] rounded-seg transition-colors">{t('commonCancel', 'Cancel')}</button>
                {/* Labels Ui update #289 (PINNED): transparent at rest, and the hover fill
                    must compute to EXACTLY rgb(75,85,99) — hence gray-600 rather than the
                    system's alpha-white hover. Documented deviation; don't "fix" it. */}
                <button
                    type="button"
                    onClick={submit}
                    disabled={!draftName.trim() || busy}
                    data-testid="label-save"
                    className="px-2.5 py-1 text-[13px] font-medium text-white hover:bg-gray-600 rounded-seg disabled:opacity-50 transition-colors"
                >
                    {mode === 'create' ? t('brdCreateAndAdd', 'Create & Add') : t('commonSave', 'Save')}
                </button>
            </div>
        </div>
    );

    return (
        <div data-testid="label-picker" className="border border-pai-subtle rounded-bubble p-4 space-y-1.5 bg-white/[0.025]">
            <div className="pai-eyebrow mb-2">{t('brdLabelsHeader', 'Labels')}</div>
            {labels.length === 0 && mode == null && (
                <div className="text-[13px] text-pai-meta">{t('brdNoLabelsYet', 'No labels yet — create one to start tagging tickets.')}</div>
            )}
            {labels.map((l) => {
                if (mode === l.id) return <div key={l.id}>{form}</div>;
                const selected = ticketLabelIds.has(l.id);
                // Checkbox-only activation (Labels Board Update #113): ONLY the checkbox
                // toggles assignment — clicking the rest of the row does nothing (no
                // accidental activations).
                return (
                    <div key={l.id} data-testid="label-row" className="flex items-center gap-2.5 px-1 py-1 text-[13px]">
                        <button
                            type="button"
                            onClick={() => onToggle(l.id)}
                            data-testid="label-checkbox"
                            role="checkbox"
                            aria-checked={selected}
                            aria-label={`${selected ? t('brdUnassignWord', 'Unassign') : t('brdAssignWord', 'Assign')} ${t('brdLabelWord', 'label')} ${l.name}`}
                            className={`flex-shrink-0 w-4 h-4 rounded-chip border flex items-center justify-center transition-colors ${
                                selected ? 'bg-white border-white text-pai-void' : 'border-white/40 bg-white/[0.03]'
                            }`}
                        >
                            {selected && <Check size={11} strokeWidth={3} />}
                        </button>
                        <span className="w-3 h-3 rounded-[4px] flex-shrink-0" style={{ background: l.color || '#6366f1' }} />
                        <span data-testid="label-row-name" className="flex-1 text-pai-body truncate">{l.name}</span>
                        <button onClick={() => startEdit(l)} title={t('brdEditLabel', 'Edit label')} aria-label={`${t('brdEditLabel', 'Edit label')} ${l.name}`} className="flex-shrink-0 p-1 text-white/45 hover:text-white transition-colors"><Pencil size={13} /></button>
                        <button onClick={() => remove(l.id)} title={t('brdDeleteLabelAction', 'Delete label')} aria-label={`${t('brdDeleteLabelAction', 'Delete label')} ${l.name}`} className="flex-shrink-0 p-1 text-white/45 hover:text-red-500 transition-colors"><Trash2 size={13} /></button>
                    </div>
                );
            })}
            {mode === 'create' ? form : (
                <button
                    onClick={startCreate}
                    data-testid="label-create-open"
                    // Labels Ui update #289 (PINNED): the label text must compute to exactly
                    // rgb(255,255,255) — plain `text-white`, never a tinted accent.
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 text-[13px] font-medium text-white hover:bg-white/[0.07] rounded-seg transition-colors"
                >
                    <Plus size={13} /> {t('brdCreateNewLabel', 'Create new label')}
                </button>
            )}
        </div>
    );
};

const TicketDetailModal = ({
    ticketId, members, labels, onClose, onUpdate, onDelete,
    onAddComment, onUpdateComment, onDeleteComment, onAttach, onAttachExisting, onRemoveAttachment,
    onCreateLabel, onUpdateLabel, onDeleteLabel,
}) => {
    const { t } = useLanguage();
    const [ticket, setTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingTitle, setEditingTitle] = useState(false);
    const [title, setTitle] = useState('');
    const [editingDesc, setEditingDesc] = useState(false);
    const [desc, setDesc] = useState('');
    const [newComment, setNewComment] = useState('');
    // #106 WYSIWYG: the comment editor is an UNCONTROLLED contentEditable, so we clear
    // it after a send by remounting it (bump the key) rather than resetting `value`.
    const [commentKey, setCommentKey] = useState(0);
    // Comment section Boards #241 — inline edit-mode for an existing comment. Only the
    // author sees the pencil; clicking it swaps that comment's body for the same WYSIWYG
    // editor seeded with its markdown, with explicit Save/Cancel (Enter-to-save NOT used
    // — safer for multi-line edits). `editingCommentDraft` holds the live markdown.
    const [editingCommentId, setEditingCommentId] = useState(null);
    const [editingCommentDraft, setEditingCommentDraft] = useState('');
    const [labelPickerOpen, setLabelPickerOpen] = useState(false);
    const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
    const fileInputRef = useRef(null);
    const descViewRef = useRef(null);   // description view (for edit-mode sizing #107)
    const [descEditMinH, setDescEditMinH] = useState(0); // captured view height (#107)
    const [previewDoc, setPreviewDoc] = useState(null);  // attachment preview (#108)
    const [attachMenuOpen, setAttachMenuOpen] = useState(false); // dual-source attach menu (#117)
    const [pickerOpen, setPickerOpen] = useState(false);         // Select-from-File-Manager (#117)
    const { user } = useAuth();
    // #282 Board Permissions: comment deletion is member-level (any member deletes any
    // comment — the route was widened to match), so the #241 viewerIsOwner derivation
    // is gone; `own` still gates the author-only edit pencil.

    // Description box size #107: entering edit mode must NOT shrink the workspace. Capture
    // the rendered view's height so the edit surface is at least as tall (spacious, no
    // collapse) — matching the size of the previous view-mode container.
    const startEditDesc = () => {
        const h = descViewRef.current ? descViewRef.current.offsetHeight : 0;
        setDescEditMinH(Math.max(h, 200));
        setEditingDesc(true);
    };

    const reload = useCallback(async () => {
        try {
            const fresh = await boardService.getTicket(ticketId);
            setTicket(fresh);
            setTitle(fresh.title || '');
            setDesc(fresh.description || '');
        } catch (e) {
            console.error('Failed to load ticket', e);
        } finally {
            setLoading(false);
        }
    }, [ticketId]);

    useEffect(() => { reload(); }, [reload]);

    // Hooks must run unconditionally (rules-of-hooks) — these useMemo/useState hooks
    // were previously called after the `if (!ticketId) return null` early return, so
    // the hook count changed when the modal opened/closed. Both memos are already
    // null-safe (ticket starts null), so hoisting above the early return is safe.
    const ticketLabelIds = useMemo(
        () => new Set((ticket?.labels || []).map((l) => l.id)),
        [ticket],
    );
    const ticketAssigneeIds = useMemo(
        () => new Set((ticket?.assignees || []).map((a) => a.user_id)),
        [ticket],
    );

    const saveTitle = async () => {
        if (!title.trim() || title === ticket.title) { setEditingTitle(false); return; }
        await onUpdate(ticketId, { title: title.trim() });
        await reload();
        setEditingTitle(false);
    };

    const saveDesc = async () => {
        if (desc === (ticket.description || '')) { setEditingDesc(false); return; }
        await onUpdate(ticketId, { description: desc });
        await reload();
        setEditingDesc(false);
    };

    // ── Selecting labels bug #288 — optimistic + coalesced label toggling ──
    // ROOT CAUSE of the old last-write-wins: each click computed the full
    // `label_ids` array from `ticketLabelIds` (derived from the SERVER ticket) and
    // then awaited PATCH + reload (~a multi-second round trip on prod). A second
    // click landing before the first reload read the STALE server set, so its
    // PATCH silently dropped the first toggle — only the last click "stuck" unless
    // the user waited 4-5s between clicks.
    // Fix: a local optimistic Set (`localLabelIds`) is the UI's source of truth
    // while edits are pending — every click registers instantly against it — and a
    // single-flight pump serializes the writes: at most ONE PATCH is in flight,
    // and clicks arriving meanwhile just update the desired set, which is re-sent
    // once the in-flight PATCH resolves (N rapid toggles coalesce into 1-2
    // requests, none lost). The override clears only after the trailing reload,
    // so the UI never flickers back to a stale server state.
    const [localLabelIds, setLocalLabelIds] = useState(null); // Set | null (null = follow server)
    const localLabelIdsRef = useRef(null);   // synchronous mirror — clicks never read a stale render
    const desiredLabelIdsRef = useRef(null);
    const labelSyncBusyRef = useRef(false);
    const effectiveLabelIds = localLabelIds ?? ticketLabelIds;
    const effectiveLabels = localLabelIds
        ? labels.filter((l) => localLabelIds.has(l.id))
        : (ticket?.labels || []);

    const pumpLabelSync = async () => {
        if (labelSyncBusyRef.current) return;
        labelSyncBusyRef.current = true;
        try {
            while (desiredLabelIdsRef.current) {
                const want = desiredLabelIdsRef.current;
                desiredLabelIdsRef.current = null;
                try {
                    await onUpdate(ticketId, { label_ids: Array.from(want) });
                } catch (err) {
                    // Drop the optimistic state and restore the server truth.
                    desiredLabelIdsRef.current = null;
                    localLabelIdsRef.current = null;
                    setLocalLabelIds(null);
                    await reload();
                    alert(prefixedUserErrorMessage(t('brdFailedUpdateLabels', 'Failed to update labels'), err));
                    return;
                }
            }
            await reload();
        } finally {
            labelSyncBusyRef.current = false;
            // A click can land while the trailing reload is in flight — pump again
            // (and keep the optimistic override) instead of clearing to stale state.
            if (desiredLabelIdsRef.current) pumpLabelSync();
            else { localLabelIdsRef.current = null; setLocalLabelIds(null); }
        }
    };

    const toggleLabel = (labelId) => {
        const next = new Set(localLabelIdsRef.current ?? ticketLabelIds);
        if (next.has(labelId)) next.delete(labelId); else next.add(labelId);
        localLabelIdsRef.current = next;   // synchronous — the very next click builds on this
        setLocalLabelIds(next);            // checkbox + chips flip immediately
        desiredLabelIdsRef.current = next; // what the server must converge to
        pumpLabelSync();
    };

    // Label repository management — the board's palette. After create/edit/delete the
    // parent reloads the board (refreshing the `labels` prop); we also reload the ticket
    // so a deleted/renamed label is reflected on its own chips immediately.
    const handleCreateLabel = async (name, color) => onCreateLabel(name, color); // returns the created label (#113 Create & Add)
    const handleUpdateLabel = async (labelId, updates) => { await onUpdateLabel(labelId, updates); await reload(); };
    const handleDeleteLabel = async (labelId) => { await onDeleteLabel(labelId); await reload(); };

    const toggleAssignee = async (userId) => {
        const next = new Set(ticketAssigneeIds);
        if (next.has(userId)) next.delete(userId); else next.add(userId);
        await onUpdate(ticketId, { assignee_ids: Array.from(next) });
        await reload();
    };

    const updateDueDate = async (value) => {
        await onUpdate(ticketId, { due_date: value || null });
        await reload();
    };

    // #106: `rawContent` comes from the WYSIWYG comment editor (its serialized markdown).
    // Called from the form-submit (send button, uses `newComment`) and the editor's
    // Enter-to-send (passes the live markdown), so it never relies on a stale state read.
    const doSubmitComment = (rawContent) => {
        const content = String(rawContent ?? newComment).trim();
        if (!content) return;
        // Optimistic: append the comment immediately. The fire-and-forget
        // API call replaces it with the persisted version once it returns.
        const tempId = `temp-${Date.now()}`;
        const placeholder = {
            id: tempId,
            ticket_id: ticketId,
            user_id: user?.id,
            username: user?.username,
            email: user?.email,
            content,
            created_at: new Date().toISOString(),
            updated_at: null,
            _pending: true,
        };
        setTicket((t) => t ? ({ ...t, comments: [...(t.comments || []), placeholder] }) : t);
        setNewComment('');
        setCommentKey((k) => k + 1); // remount → clears the uncontrolled editor
        onAddComment(ticketId, content)
            .then(() => reload())
            .catch((err) => {
                setTicket((t) => t ? ({ ...t, comments: (t.comments || []).filter((c) => c.id !== tempId) }) : t);
                alert(prefixedUserErrorMessage(t('brdFailedAddComment', 'Failed to add comment'), err));
            });
    };
    const submitComment = (e) => { e?.preventDefault?.(); doSubmitComment(newComment); };

    // Comment deletion persistence bug: the parent onDeleteComment only hits the API — it
    // never touched the modal's local `ticket.comments`, so a deleted comment lingered until
    // close/reopen re-fetched. Mirror submitComment: optimistically remove immediately, then
    // reload() to sync; on failure reload() to restore the (undeleted) truth.
    const deleteCommentLocal = (commentId) => {
        setTicket((t) => t ? ({ ...t, comments: (t.comments || []).filter((c) => c.id !== commentId) }) : t);
        onDeleteComment(commentId)
            .then(() => reload())
            .catch((err) => {
                reload();
                alert(prefixedUserErrorMessage(t('brdFailedDeleteComment', 'Failed to delete comment'), err));
            });
    };

    // Comment section Boards #241 — inline comment editing (author-only). Mirrors the
    // add/delete flows: optimistically patch the local `ticket.comments` on save, then
    // reload() to sync (on failure reload() restores the persisted truth).
    const startEditComment = (c) => {
        setEditingCommentId(c.id);
        setEditingCommentDraft(c.content || '');
    };
    const cancelEditComment = () => {
        setEditingCommentId(null);
        setEditingCommentDraft('');
    };
    const saveEditComment = (commentId) => {
        const content = String(editingCommentDraft ?? '').trim();
        if (!content) return; // empty edit → treat as no-op (delete has its own button)
        setEditingCommentId(null);
        setEditingCommentDraft('');
        setTicket((t) => t ? ({
            ...t,
            comments: (t.comments || []).map((c) => (
                c.id === commentId ? { ...c, content, updated_at: new Date().toISOString() } : c
            )),
        }) : t);
        onUpdateComment(commentId, content)
            .then(() => reload())
            .catch((err) => {
                reload();
                alert(prefixedUserErrorMessage(t('brdFailedEditComment', 'Failed to edit comment'), err));
            });
    };

    const [attaching, setAttaching] = useState(false);
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAttaching(true);
        try {
            await onAttach(ticketId, file);
            await reload();
        } catch (err) {
            alert(prefixedUserErrorMessage(t('brdFailedAttachFile', 'Failed to attach file'), err));
        } finally {
            setAttaching(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    // All hooks are above this point; the early return is now placed after them so the
    // hook order is stable across open/close renders (rules-of-hooks).
    if (!ticketId) return null;

    // PORTALED to <body> by <Modal> (the #225 pattern). In expanded mode the panel
    // wrapper keeps a `transform: translateY(0)` from its `forwards` open
    // animation, which makes it a containing block for `position: fixed` — so
    // the dialog was being sized by the PANEL, not the viewport, and could never
    // reach the design's 860px. Full-screen on a phone, centred from `sm:`.
    // Escape is NOT the Modal's: the inline title/description editors use it to
    // cancel an edit (and PanelShell's own handler then closes the panel).
    return (
        <>
        <Modal
            onClose={onClose}
            placement="fill"
            size="none"
            layout="column"
            closeOnBackdrop={false}
            closeOnEscape={false}
            className="sm:items-center sm:p-10"
            surface="bg-pai-modal sm:rounded-modal shadow-modal border border-pai"
            cardClassName="w-full sm:max-w-[860px] max-h-dvh sm:max-h-[90dvh]"
            cardTestId="ticket-detail-modal"
        >
                <div className="px-8 pt-7 pb-5 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        {/* "Ticket View" terminology for the expanded card (Labels Board Update #113) */}
                        <div data-testid="ticket-view-label" className="pai-eyebrow">{t('brdTicketViewLabel', 'Ticket View')}</div>
                        {editingTitle ? (
                            <input
                                autoFocus
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                onBlur={saveTitle}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveTitle();
                                    if (e.key === 'Escape') { setTitle(ticket.title); setEditingTitle(false); }
                                }}
                                className="pai-input mt-1.5 w-full px-2.5 py-1 text-[28px] font-extrabold tracking-[-0.025em] leading-[1.2]"
                            />
                        ) : (
                            <h2
                                className="mt-1.5 text-[28px] font-extrabold tracking-[-0.025em] leading-[1.2] text-white truncate cursor-text"
                                onClick={() => setEditingTitle(true)}
                                title={t('brdClickToRename', 'Click to rename')}
                            >
                                {ticket?.title || t('brdLoading', 'Loading...')}
                            </h2>
                        )}
                        {/* Author attribution (#415). A ticket written by someone
                            who has since deleted their account SURVIVES here —
                            the team keeps the work — and reads "Deleted user"
                            rather than blank. */}
                        {ticket && (
                            <div
                                data-testid="ticket-created-by"
                                data-deleted-author={!ticket.created_by_name}
                                className="mt-1.5 text-[12px] text-pai-secondary/70 truncate"
                            >
                                {t('brdTicketCreatedBy', 'Created by')}{' '}
                                <span className="text-pai-secondary">
                                    {ticketCreatorLabel(ticket, t)}
                                </span>
                            </div>
                        )}
                    </div>
                    <button onClick={onClose} data-testid="ticket-modal-close" className="pai-icon-btn w-[38px] h-[38px] flex-shrink-0 self-start -mr-1.5">
                        <X size={18} />
                    </button>
                </div>

                {loading || !ticket ? (
                    <div className="flex-1 flex items-center justify-center text-[15px] text-pai-meta">{t('brdLoading', 'Loading...')}</div>
                ) : (
                    <div className="flex-1 overflow-y-auto px-8 pb-8 space-y-5">
                        {/* Property bar (#280 / PAI handoff): one row of bordered pill buttons
                            above a hairline, with the delete bin right-aligned. */}
                        <div className="flex flex-wrap items-center gap-2.5 pt-5 border-t border-white/[0.08]">
                            <button
                                onClick={() => setLabelPickerOpen((v) => !v)}
                                className="flex items-center gap-2 h-10 px-[15px] text-[13.5px] font-semibold rounded-ctl border border-pai text-white hover:bg-white/[0.07] hover:border-pai-strong transition-colors"
                            >
                                <Tag size={16} />
                                {/* #288: count derives from the optimistic set so it tracks rapid toggles live */}
                                {t('brdLabelsHeader', 'Labels')} {effectiveLabels.length ? `(${effectiveLabels.length})` : ''}
                            </button>
                            <button
                                onClick={() => setAssigneePickerOpen((v) => !v)}
                                className="flex items-center gap-2 h-10 px-[15px] text-[13.5px] font-semibold rounded-ctl border border-pai text-white hover:bg-white/[0.07] hover:border-pai-strong transition-colors"
                            >
                                <Users size={16} />
                                {t('brdAssigneesHeader', 'Assignees')} {ticket.assignees?.length ? `(${ticket.assignees.length})` : ''}
                            </button>
                            <label className="flex items-center gap-2 h-10 px-[15px] text-[13.5px] rounded-ctl border border-pai text-pai-meta hover:bg-white/[0.07] hover:border-pai-strong transition-colors cursor-pointer">
                                <CalendarIcon size={16} />
                                <input
                                    type="date"
                                    value={ticket.due_date ? ticket.due_date.slice(0, 10) : ''}
                                    onChange={(e) => updateDueDate(e.target.value)}
                                    className="bg-transparent text-[13.5px] font-medium text-white tnum outline-none border-0 p-0 cursor-pointer"
                                    style={{ colorScheme: 'dark' }}
                                />
                            </label>
                            {/* Dual-source attach (Document attachment Boards #117): Upload from
                                Device (hash-dedup) OR Select from File Manager (link existing). */}
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => setAttachMenuOpen((v) => !v)}
                                    data-testid="ticket-attach-button"
                                    disabled={attaching}
                                    className="flex items-center gap-2 h-10 px-[15px] text-[13.5px] font-semibold rounded-ctl border border-pai text-white hover:bg-white/[0.07] hover:border-pai-strong transition-colors"
                                >
                                    {attaching ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />} {attaching ? t('brdAttachingEllipsis', 'Attaching…') : t('brdAttachFile', 'Attach file')}
                                </button>
                                <input ref={fileInputRef} type="file" data-testid="ticket-attach-input" onChange={handleFileUpload} disabled={attaching} className="hidden" />
                                {attachMenuOpen && (
                                    <div className="absolute left-0 top-11 z-30 w-56 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5">
                                        <button
                                            type="button"
                                            data-testid="attach-upload-device"
                                            onClick={() => { setAttachMenuOpen(false); fileInputRef.current?.click(); }}
                                            className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white flex items-center gap-2.5 transition-colors"
                                        >
                                            <Download size={14} className="rotate-180" /> {t('brdUploadFromDevice', 'Upload from Device')}
                                        </button>
                                        <button
                                            type="button"
                                            data-testid="attach-from-fm"
                                            onClick={() => { setAttachMenuOpen(false); setPickerOpen(true); }}
                                            className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white flex items-center gap-2.5 transition-colors"
                                        >
                                            <FileText size={14} /> {t('brdSelectFromFileManager', 'Select from File Manager')}
                                        </button>
                                    </div>
                                )}
                            </div>
                            {/* Ticket UI #116: icon-only trash bin — white/neutral lines by
                                default, turns red ONLY on hover (per the design mockup). */}
                            <button
                                onClick={() => onDelete(ticketId)}
                                data-testid="ticket-delete-btn"
                                aria-label={t('brdDeleteTicket', 'Delete ticket')}
                                title={t('brdDeleteTicket', 'Delete ticket')}
                                // #116 (PINNED): neutral at rest, EXACTLY red-500 on hover.
                                className="ml-auto flex items-center justify-center w-10 h-10 rounded-ctl text-white hover:text-red-500 hover:bg-pai-red/[0.14] transition-colors"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>

                        {/* Zero-click assigned labels (Labels Board Update #113): the ticket's
                            labels show immediately in the Ticket View, under the Labels button
                            and above the Description — no need to open the picker. */}
                        {effectiveLabels.length > 0 && (
                            <div data-testid="ticket-labels" className="flex flex-wrap gap-2">
                                {/* The applied chip is the larger variant (13px) — same filled
                                    exception as the card chips; hex untouched, ink derived. */}
                                {effectiveLabels.map((l) => (
                                    <span
                                        key={l.id}
                                        className="text-[13px] font-bold leading-none px-[13px] py-1.5 rounded-[7px]"
                                        style={{ background: l.color || '#6366f1', color: labelInkFor(l.color || '#6366f1') }}
                                    >
                                        {l.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {labelPickerOpen && (
                            <LabelPicker
                                labels={labels}
                                ticketLabelIds={effectiveLabelIds}
                                onToggle={toggleLabel}
                                onCreate={handleCreateLabel}
                                onUpdate={handleUpdateLabel}
                                onDelete={handleDeleteLabel}
                            />
                        )}

                        {assigneePickerOpen && (
                            <div className="border border-pai-subtle rounded-bubble p-4 space-y-1.5 bg-white/[0.025]">
                                <div className="pai-eyebrow mb-2">{t('brdAssigneesHeader', 'Assignees')}</div>
                                {members.length === 0 && <div className="text-[13px] text-pai-meta">{t('brdNoBoardMembers', 'No board members.')}</div>}
                                {members.map((m) => {
                                    const selected = ticketAssigneeIds.has(m.user_id);
                                    return (
                                        <button
                                            key={m.user_id}
                                            onClick={() => toggleAssignee(m.user_id)}
                                            className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-seg text-left text-[13px] transition-colors ${
                                                selected ? 'bg-white/[0.07]' : 'hover:bg-white/[0.05]'
                                            }`}
                                        >
                                            {/* #258: same global avatar style + shared initials
                                                as the card chips and the rest of the app. */}
                                            <span
                                                data-testid="assignee-picker-avatar"
                                                className="w-5 h-5 rounded-full bg-white text-pai-void text-[9px] flex items-center justify-center font-bold select-none"
                                            >
                                                {avatarInitials(m)}
                                            </span>
                                            <span className="text-pai-body truncate">
                                                {m.username || m.email}
                                            </span>
                                            {selected && <Check size={13} strokeWidth={3} className="ml-auto flex-shrink-0 text-white" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Description */}
                        <div>
                            <div className="pai-eyebrow mb-2">{t('commonDescription', 'Description')}</div>
                            {editingDesc ? (
                                // #106 WYSIWYG: a contentEditable editor renders styled text as
                                // you type (headings big, bold bold, lists as bullets) — the user
                                // NEVER sees raw `#`/`**`/`-`. It seeds from the stored markdown so
                                // edit-mode looks identical to the saved view, and pushes serialized
                                // markdown up via onChange so `desc`/save stay markdown. minHeight
                                // keeps the box spacious (#107).
                                <div>
                                    <RichTextEditor
                                        toolbarTestId="desc-toolbar"
                                        editorTestId="desc-editor"
                                        initialMarkdown={desc}
                                        onChange={setDesc}
                                        placeholder={t('brdDescPlaceholder', 'Add a description... (use the toolbar — no need to type markdown)')}
                                        minHeight={Math.max(descEditMinH || 0, 200)}
                                        ariaLabel={t('brdTicketDescriptionAria', 'Ticket description')}
                                    />
                                    <div className="mt-2.5 flex gap-2.5 justify-end">
                                        {/* Handoff footer: secondary Cancel + primary white Save.
                                            #280 (PINNED) only requires Save is not indigo. */}
                                        <button onClick={() => { setDesc(ticket.description || ''); setEditingDesc(false); }} className="pai-btn-ghost h-9 px-4 text-[13.5px]">{t('commonCancel', 'Cancel')}</button>
                                        <button onClick={saveDesc} className="pai-btn-primary h-9 px-6 text-[13.5px]">{t('commonSave', 'Save')}</button>
                                    </div>
                                </div>
                            ) : (
                                // Update text description #105: render the description as MARKDOWN
                                // (headers/lists/bold, raw symbols hidden) with the same react-markdown
                                // + remark-gfm + prose stack the main chat uses. Click anywhere to edit.
                                <div
                                    ref={descViewRef}
                                    onClick={startEditDesc}
                                    role="button"
                                    tabIndex={0}
                                    data-testid="ticket-description-view"
                                    // Ticket detail Update #280: the description is a clearly
                                    // BORDERED box (per the mockup) whose surface matches the
                                    // edit-mode editor container — view ↔ edit look identical.
                                    // #280 (PINNED): a clearly BORDERED box (solid, ≥1px) whose
                                    // surface matches the edit-mode editor container.
                                    className="w-full text-left px-[22px] py-5 text-[15px] leading-[1.6] bg-white/[0.02] hover:bg-white/[0.04] rounded-bubble border border-white/[0.1] text-pai-body min-h-[200px] cursor-text transition-colors"
                                >
                                    {ticket.description ? (
                                        // `dark:prose-invert` is KEPT deliberately: the #287
                                        // inline-code fixes in tailwind.config.js are generated
                                        // against that exact selector. Don't drop the variant.
                                        <div className="prose prose-sm max-w-none dark:prose-invert">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {ticket.description}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <span className="text-pai-placeholder italic">{t('brdAddDescriptionPlaceholder', 'Add a description...')}</span>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Attachments (Ticket attachments Boards #108): compact filename-only
                            list — NO thumbnails. Click the filename to open the full document
                            viewer (multi-page scroll, same as the File Manager). A Download icon
                            sits left of an icon-only trash (white/neutral, red ONLY on hover). */}
                        {ticket.attachments && ticket.attachments.length > 0 && (
                            <div data-testid="ticket-attachments">
                                <div className="pai-eyebrow mb-2.5">{t('brdAttachmentsHeader', 'Attachments')}</div>
                                <div className="space-y-1.5">
                                    {ticket.attachments.map((a) => (
                                        <div key={a.id} data-testid="ticket-attachment" className="flex items-center gap-2.5 px-3 py-2.5 bg-white/[0.025] border border-pai-hairline rounded-row">
                                            <Paperclip size={14} className="text-white/45 flex-shrink-0" />
                                            <button
                                                type="button"
                                                onClick={() => setPreviewDoc({ id: a.document_id, name: a.filename })}
                                                data-testid="ticket-attachment-name"
                                                className="flex-1 min-w-0 text-left text-[13.5px] font-semibold text-white truncate hover:underline"
                                                title={a.filename}
                                            >
                                                {a.filename}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => documentService.downloadDocument(a.document_id, a.filename)}
                                                data-testid="ticket-attachment-download"
                                                aria-label={`${t('commonDownload', 'Download')} ${a.filename}`}
                                                title={t('commonDownload', 'Download')}
                                                className="flex-shrink-0 p-1 rounded-chip text-white hover:text-pai-blue transition-colors"
                                            >
                                                <Download size={15} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={async () => { await onRemoveAttachment(a.id); await reload(); }}
                                                data-testid="ticket-attachment-delete"
                                                aria-label={`${t('commonRemove', 'Remove')} ${a.filename}`}
                                                title={t('brdRemoveAttachment', 'Remove attachment')}
                                                // #108 (PINNED): neutral at rest, EXACTLY red-500 on hover.
                                                className="flex-shrink-0 p-1 rounded-chip text-white hover:text-red-500 transition-colors"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Comments */}
                        <div>
                            <div className="pai-eyebrow mb-2.5">
                                {t('brdCommentsHeader', 'Comments')} {ticket.comments?.length ? `(${ticket.comments.length})` : ''}
                            </div>
                            {/* #282 Board Permissions — ANY board member can delete ANY
                                comment (mirrors the widened DELETE route; supersedes #241's
                                author-or-owner rule). Only the author sees the pencil (edit
                                stays author-only, mirroring the PATCH route). */}
                            <div className="space-y-0">
                                {(ticket.comments || []).map((c, idx) => {
                                    const own = c.user_id === user?.id;
                                    const pending = !!c._pending;
                                    // #282: everyone who can open the ticket is a member,
                                    // and any member may delete any comment.
                                    const canDelete = true;
                                    const editing = editingCommentId === c.id;
                                    return (
                                        // #241: a thin top line separates individual comments so
                                        // they don't read as one stacked block (skip the first —
                                        // it sits directly under the "Comments" header).
                                        <div
                                            key={c.id}
                                            data-testid="comment-item"
                                            // #241 (PINNED): the 2nd+ comment carries a top border.
                                            className={`flex gap-2.5 py-3 ${idx > 0 ? 'border-t border-white/[0.08]' : ''} ${pending ? 'opacity-60' : ''}`}
                                        >
                                            {/* Matching balls boards #109: white avatar + shared initials (parity w/ main UI) */}
                                            <div data-testid="comment-avatar" className="w-7 h-7 rounded-full bg-white text-pai-void text-[10px] flex items-center justify-center font-bold flex-shrink-0 select-none">
                                                {avatarInitials(c)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                {/* #241 layout: user name on top row (with timestamp
                                                    + action icons), comment content underneath. */}
                                                <div className="text-[12.5px] text-pai-meta flex items-center gap-2">
                                                    <span className="font-semibold text-pai-body">{c.username || c.email || t('brdSomeoneFallback', 'Someone')}</span>
                                                    <span className="text-white/25">·</span>
                                                    <span data-testid="comment-timestamp" className="tnum">{pending ? t('brdSendingEllipsis', 'sending...') : formatCommentTimestamp(c.created_at)}</span>
                                                    {!pending && !editing && (own || canDelete) && (
                                                        <span className="ml-auto flex items-center gap-1.5">
                                                            {own && (
                                                                <button
                                                                    type="button"
                                                                    data-testid="comment-edit-btn"
                                                                    onClick={() => startEditComment(c)}
                                                                    aria-label={t('brdEditComment', 'Edit comment')}
                                                                    title={t('brdEditComment', 'Edit comment')}
                                                                    // #241 (PINNED): neutral at rest, EXACTLY blue-500 on hover.
                                                                    className="p-0.5 rounded-chip bg-transparent text-white hover:text-blue-500 transition-colors"
                                                                >
                                                                    <Pencil size={14} />
                                                                </button>
                                                            )}
                                                            {canDelete && (
                                                                <button
                                                                    type="button"
                                                                    data-testid="comment-delete-btn"
                                                                    onClick={() => deleteCommentLocal(c.id)}
                                                                    aria-label={t('brdDeleteComment', 'Delete comment')}
                                                                    title={t('brdDeleteComment', 'Delete comment')}
                                                                    // #241 (PINNED): neutral at rest, EXACTLY red-500 on hover.
                                                                    className="p-0.5 rounded-chip bg-transparent text-white hover:text-red-500 transition-colors"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                                {editing ? (
                                                    // #241 edit mode: reuse the WYSIWYG editor (same as
                                                    // the composer) seeded with the comment's markdown,
                                                    // explicit Save / Cancel.
                                                    <div data-testid="comment-edit-mode" className="mt-1">
                                                        <RichTextEditor
                                                            key={`edit-${c.id}`}
                                                            toolbarTestId="comment-edit-toolbar"
                                                            editorTestId="comment-edit-editor"
                                                            initialMarkdown={c.content || ''}
                                                            onChange={setEditingCommentDraft}
                                                            placeholder={t('brdEditCommentPlaceholder', 'Edit comment...')}
                                                            ariaLabel={t('brdEditComment', 'Edit comment')}
                                                        />
                                                        <div className="mt-1.5 flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                data-testid="comment-edit-save"
                                                                onClick={() => saveEditComment(c.id)}
                                                                disabled={!String(editingCommentDraft || '').trim()}
                                                                className="px-4 py-1.5 text-[13px] font-semibold rounded-seg bg-white text-pai-void hover:bg-white/[0.88] disabled:opacity-40 transition-colors"
                                                            >
                                                                {t('commonSave', 'Save')}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                data-testid="comment-edit-cancel"
                                                                onClick={cancelEditComment}
                                                                className="px-4 py-1.5 text-[13px] font-semibold rounded-seg border border-pai text-pai-secondary hover:text-white hover:border-pai-strong transition-colors"
                                                            >
                                                                {t('commonCancel', 'Cancel')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div data-testid="ticket-comment-body" className="mt-1 text-[15px] leading-[1.6] text-pai-body prose prose-sm max-w-none dark:prose-invert">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{c.content || ''}</ReactMarkdown>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <form onSubmit={submitComment} className="mt-3 flex gap-2 items-start">
                                {/* Matching balls boards #109: comment-input avatar = white + shared initials (1:1 with the main-UI corner avatar) */}
                                <div data-testid="comment-input-avatar" className="w-7 h-7 rounded-full bg-white text-pai-void text-[10px] flex items-center justify-center font-bold flex-shrink-0 select-none">
                                    {avatarInitials(user)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    {/* #106 WYSIWYG comment composer: same contentEditable editor as
                                        the description — the toolbar applies real bold/lists/headings
                                        (no raw markdown). #116: plain Enter SENDS (submitOnEnter);
                                        Shift+Enter inserts a newline / next list item. */}
                                    <RichTextEditor
                                        key={commentKey}
                                        toolbarTestId="comment-toolbar"
                                        editorTestId="comment-editor"
                                        initialMarkdown=""
                                        onChange={setNewComment}
                                        onSubmit={doSubmitComment}
                                        submitOnEnter
                                        // Ticket detail Update #280 (PO deviation from the mockup):
                                        // the formatting toolbar stays HIDDEN until the user clicks
                                        // into the composer — matching the Description field, whose
                                        // toolbar only exists in edit mode.
                                        collapsibleToolbar
                                        placeholder={t('brdWriteCommentPlaceholder', 'Write a comment... (Enter to send)')}
                                        ariaLabel={t('brdWriteCommentAria', 'Write a comment')}
                                    />
                                </div>
                                {/* Paper plane cleanup #134: icon-only Send — NO purple box /
                                    container. Neutral (gray in light, white in dark) following the
                                    trash-icon pattern; subtle color-shift + faint glow on hover so
                                    it still reads as interactive. self-center so the plane sits
                                    naturally, vertically centered in the comment input area. */}
                                <button
                                    type="submit"
                                    disabled={!newComment.trim()}
                                    data-testid="comment-send-btn"
                                    aria-label={t('brdSendComment', 'Send comment')}
                                    // #134 (PINNED): NO background container — the computed
                                    // background must stay fully transparent.
                                    className="self-center flex-shrink-0 p-1.5 bg-transparent rounded-ctl text-white/70 hover:text-white hover:drop-shadow-[0_0_6px_rgba(255,255,255,0.35)] disabled:opacity-40 disabled:hover:text-white/70 transition-all"
                                    title={t('brdSend', 'Send')}
                                >
                                    <Send size={18} />
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Full document viewer for an attachment (Ticket attachments Boards #108) */}
            {previewDoc && (
                <DocumentPreviewModal
                    documentId={previewDoc.id}
                    documentName={previewDoc.name}
                    onClose={() => setPreviewDoc(null)}
                />
            )}

            {/* Select from File Manager picker (Document attachment Boards #117) */}
            {pickerOpen && (
                <AttachPickerModal
                    onClose={() => setPickerOpen(false)}
                    onSelect={async (documentId) => {
                        await onAttachExisting(ticketId, documentId);
                        await reload();
                        setPickerOpen(false);
                    }}
                />
            )}
        </>
    );
};


// ─────────────────────────────────────────────────────────────────────────
// Single board (kanban) view
// ─────────────────────────────────────────────────────────────────────────

const BoardView = ({ boardId, onBack, onClose, onToggleExpand, mode }) => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const [data, setData] = useState(null);  // { board, columns, labels, members }
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openTicketId, setOpenTicketId] = useState(null);
    const [showShare, setShowShare] = useState(false);
    const [draggingTicketId, setDraggingTicketId] = useState(null);
    const [draggingColumnId, setDraggingColumnId] = useState(null);
    // Scrolling Kanban #491: continuous edge auto-scroll while a ticket or column is dragged
    // (horizontal on the scroller; vertical inside the column under the pointer for tickets).
    const kanbanScrollerRef = useRef(null);
    useDragAutoScroll(kanbanScrollerRef, Boolean(draggingTicketId || draggingColumnId), { vertical: Boolean(draggingTicketId) });
    const [movingColumn, setMovingColumn] = useState(null); // {id,name} — Manual column move #110
    const [movingTicket, setMovingTicket] = useState(null); // ticket — Manual Ticket movement #136
    const [newColumnComposing, setNewColumnComposing] = useState(false);
    const [newColumnName, setNewColumnName] = useState('');
    const [search, setSearch] = useState('');  // in-board ticket search (#112)
    const [searchFocused, setSearchFocused] = useState(false);  // #222: expand search on focus, yield title space
    // COMPACT header (#222, re-tuned for the PAI redesign). The single-row header
    // (#112) has a fixed cost — back + icon + name, then members/kebab/expand/close
    // — and the redesign shrank every docked panel by ~230px (a 250px nav sidebar
    // where a 64px icon rail used to be, plus the 14px frame and gutters). At the
    // resulting widths that fixed cost exceeded the row, so the row OVERFLOWED: the
    // flex-1 search collapsed to its own padding floor and the shrink-0 member
    // controls spilled out from under it — exactly the #222 overlap, back again for
    // a new reason. Below the threshold the chrome yields instead: tighter padding,
    // 32px icon buttons, and a title capped in absolute terms rather than at a
    // percentage of a box that is itself too small.
    const [compactHeader, setCompactHeader] = useState(false);
    const headerRoRef = useRef(null);
    // A CALLBACK ref, not useRef + an effect: BoardView early-returns a loading
    // state before the header exists, so a mount-time effect finds a null ref and
    // — with empty deps — never looks again. A callback ref fires when the node
    // actually appears.
    const headerRowRef = useCallback((node) => {
        if (headerRoRef.current) {
            headerRoRef.current.disconnect();
            headerRoRef.current = null;
        }
        if (!node || typeof ResizeObserver === 'undefined') return;
        // Container-driven, never a viewport breakpoint: a docked split panel is
        // narrow while the window is wide (the #142/#231 precedent).
        // Measured on the BORDER box — compact mode changes this row's own padding,
        // so keying off the content box would let the switch feed back into the
        // measurement and oscillate around the threshold.
        const ro = new ResizeObserver(() => {
            setCompactHeader(node.getBoundingClientRect().width < 500);
        });
        ro.observe(node);
        headerRoRef.current = ro;
    }, []);
    // Board settings kebab menu (Manual Board deletion, rename and Menu #101)
    const [boardMenuOpen, setBoardMenuOpen] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    // #274 Leave Board — the manual-exit confirmation modal + its state.
    const [confirmingLeave, setConfirmingLeave] = useState(false);
    const [leaving, setLeaving] = useState(false);
    const [leaveError, setLeaveError] = useState('');
    const boardMenuRef = useRef(null);

    useEffect(() => {
        if (!boardMenuOpen) return;
        const handle = (e) => {
            if (boardMenuRef.current && !boardMenuRef.current.contains(e.target)) setBoardMenuOpen(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, [boardMenuOpen]);

    const load = useCallback(async () => {
        try {
            const fresh = await boardService.getBoardFull(boardId);
            setData(fresh);
            setError('');
        } catch (e) {
            setError(userErrorMessage(e, t));
        } finally {
            setLoading(false);
        }
    }, [boardId, t]);

    useEffect(() => { load(); }, [load]);

    // AI-driven SSE event listeners. One omnis:* event fires per ticket; a
    // batch (e.g. 15-ticket update) would otherwise trigger 15 full board
    // hydrations. Keep the board-scope guard synchronous, but trailing-debounce
    // the actual load() so a burst → one hydration.
    useEffect(() => {
        const debouncedLoad = debounce(() => load(), 300);
        const refresh = (evt) => {
            const detail = evt.detail || {};
            if (!detail.board_id || detail.board_id === boardId) debouncedLoad();
        };
        window.addEventListener('omnis:board-created', refresh);
        // board-updated covers chat-driven board/column renames (rename_board / rename_column
        // #172) so an OPEN board reflects the new name live, not just the boards-list counts.
        window.addEventListener('omnis:board-updated', refresh);
        window.addEventListener('omnis:ticket-created', refresh);
        window.addEventListener('omnis:ticket-updated', refresh);
        window.addEventListener('omnis:ticket-deleted', refresh);
        window.addEventListener('omnis:comment-added', refresh);
        window.addEventListener('omnis:ticket-attachment-added', refresh);
        // Chat-driven detach (Remove attached documents #133) — refresh the
        // paperclip count/list the instant PAI unlinks a doc from a ticket.
        window.addEventListener('omnis:ticket-attachment-removed', refresh);
        return () => {
            debouncedLoad.cancel();
            window.removeEventListener('omnis:board-created', refresh);
            window.removeEventListener('omnis:board-updated', refresh);
            window.removeEventListener('omnis:ticket-created', refresh);
            window.removeEventListener('omnis:ticket-updated', refresh);
            window.removeEventListener('omnis:ticket-deleted', refresh);
            window.removeEventListener('omnis:comment-added', refresh);
            window.removeEventListener('omnis:ticket-attachment-added', refresh);
            window.removeEventListener('omnis:ticket-attachment-removed', refresh);
        };
    }, [boardId, load]);

    // ── Local-state mutators for optimistic updates ───────────────────
    // These all touch `data` (the full board hydration) directly so the UI
    // reflects the change *before* the API roundtrip completes. Slow
    // internet no longer means a flicker / "did my click register?"
    // ambiguity — and rapid-fire ticket entry just works.

    const tempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const upsertTicketLocal = (columnId, ticket) => {
        setData((d) => {
            if (!d) return d;
            return {
                ...d,
                columns: d.columns.map((c) =>
                    c.id === columnId
                        ? { ...c, tickets: [...(c.tickets || []), ticket] }
                        : c,
                ),
            };
        });
    };

    const replaceTicketLocal = (columnId, oldId, real) => {
        setData((d) => {
            if (!d) return d;
            return {
                ...d,
                columns: d.columns.map((c) => {
                    if (c.id !== columnId) return c;
                    const existing = c.tickets || [];
                    const hasPlaceholder = existing.some((t) => t.id === oldId);
                    const alreadyHasReal = existing.some((t) => t.id === real.id);
                    let nextTickets;
                    if (hasPlaceholder) {
                        // Normal path: swap the placeholder for the real ticket.
                        nextTickets = existing.map((t) => t.id === oldId ? real : t);
                    } else if (alreadyHasReal) {
                        // A parallel load() already included the real ticket
                        // and removed our placeholder. Nothing to do.
                        return c;
                    } else {
                        // Placeholder was wiped (e.g. by a load()) before the
                        // API responded — recover by appending the real ticket
                        // so we don't drop the user's create.
                        nextTickets = [...existing, real];
                    }
                    return { ...c, tickets: nextTickets };
                }),
            };
        });
    };

    const removeTicketLocal = (columnId, ticketId) => {
        setData((d) => {
            if (!d) return d;
            return {
                ...d,
                columns: d.columns.map((c) =>
                    c.id === columnId
                        ? { ...c, tickets: (c.tickets || []).filter((t) => t.id !== ticketId) }
                        : c,
                ),
            };
        });
    };

    const moveTicketLocal = (ticketId, fromColumnId, toColumnId) => {
        setData((d) => {
            if (!d) return d;
            let moving = null;
            const stripped = d.columns.map((c) => {
                if (c.id !== fromColumnId) return c;
                const next = (c.tickets || []).filter((t) => {
                    if (t.id === ticketId) { moving = t; return false; }
                    return true;
                });
                return { ...c, tickets: next };
            });
            if (!moving) return d;
            return {
                ...d,
                columns: stripped.map((c) =>
                    c.id === toColumnId
                        ? { ...c, tickets: [...(c.tickets || []), { ...moving, column_id: toColumnId }] }
                        : c,
                ),
            };
        });
    };

    // ── Public handlers ───────────────────────────────────────────────

    const addTicket = (columnId, title) => {
        const tid = tempId();
        // Optimistic placeholder — visually faded until the API returns.
        upsertTicketLocal(columnId, {
            id: tid, column_id: columnId, title, description: null,
            position: 9999, cover_document_id: null, due_date: null,
            is_archived: false, comment_count: 0, attachment_count: 0,
            labels: [], assignees: [], _pending: true,
        });
        // Fire-and-forget — we don't block the composer.
        boardService.createTicket(columnId, { title })
            .then((real) => replaceTicketLocal(columnId, tid, real))
            .catch((err) => {
                removeTicketLocal(columnId, tid);
                alert(prefixedUserErrorMessage(t('brdFailedAddTicket', 'Failed to add ticket'), err));
            });
    };

    const addColumn = (e) => {
        e?.preventDefault?.();
        const name = newColumnName.trim();
        if (!name) { setNewColumnComposing(false); return; }
        setNewColumnName('');
        setNewColumnComposing(false);

        const tid = tempId();
        setData((d) => d ? ({
            ...d,
            columns: [...d.columns, {
                id: tid, board_id: boardId, name, position: 9999,
                color: null, ticket_count: 0, tickets: [], _pending: true,
            }],
        }) : d);

        boardService.createColumn(boardId, { name })
            .then((real) => {
                setData((d) => d ? ({
                    ...d,
                    columns: d.columns.map((c) =>
                        c.id === tid ? { ...real, tickets: [] } : c,
                    ),
                }) : d);
            })
            .catch((err) => {
                setData((d) => d ? ({
                    ...d,
                    columns: d.columns.filter((c) => c.id !== tid),
                }) : d);
                alert(prefixedUserErrorMessage(t('brdFailedAddColumn', 'Failed to add column'), err));
            });
    };

    const renameColumn = async (columnId, name) => {
        // Optimistic — local update first
        setData((d) => d ? ({
            ...d,
            columns: d.columns.map((c) => c.id === columnId ? { ...c, name } : c),
        }) : d);
        try {
            await boardService.updateColumn(columnId, { name });
        } catch (err) {
            alert(prefixedUserErrorMessage(t('brdFailedRename', 'Failed to rename'), err));
            await load();
        }
    };

    const deleteColumn = async (columnId) => {
        if (!confirm(t('brdConfirmDeleteColumn', 'Delete this column and all its tickets?'))) return;
        // Optimistic removal
        const snapshot = data;
        setData((d) => d ? ({
            ...d,
            columns: d.columns.filter((c) => c.id !== columnId),
        }) : d);
        try {
            await boardService.deleteColumn(columnId);
        } catch (err) {
            setData(snapshot);
            alert(prefixedUserErrorMessage(t('brdFailedDeleteColumn', 'Failed to delete column'), err));
        }
    };

    const moveTicket = (ticketId, columnId) => {
        // Clear the drag flag at the terminal DROP event, NOT only via the card's
        // onDragEnd (Ticket Text Color Desync bug): a cross-column move unmounts the
        // dragged card's source DOM node before the browser fires `dragend`, so that
        // handler never runs and `draggingTicketId` stays set — leaving the re-mounted
        // card (same id, new column) stuck with the `dragging` opacity-40, which dims
        // its white title to gray in Dark Mode until the board is reopened. The drop
        // handler always fires on the target, so clearing here is reliable.
        setDraggingTicketId(null);
        // Find the source column so we can roll back on error.
        let sourceColumnId = null;
        if (data) {
            for (const c of data.columns) {
                if ((c.tickets || []).some((t) => t.id === ticketId)) {
                    sourceColumnId = c.id; break;
                }
            }
        }
        if (!sourceColumnId || sourceColumnId === columnId) return;

        moveTicketLocal(ticketId, sourceColumnId, columnId);
        boardService.updateTicket(ticketId, { column_id: columnId }).catch((err) => {
            // Roll back the move
            moveTicketLocal(ticketId, columnId, sourceColumnId);
            alert(prefixedUserErrorMessage(t('brdFailedMoveTicket', 'Failed to move ticket'), err));
        });
    };

    // Manual vertical reordering within a column (Manual Drag & Drop Sorting #90):
    // dropping a ticket ONTO another inserts it just before that one. Works within a
    // column (priority sort) and across columns (drop at a precise slot). The new
    // order is persisted via the bulk /tickets/reorder endpoint so it survives reload.
    const handleReorderDrop = (draggedId, targetTicketId, position = 'before') => {
        // Clear the drag flag on the terminal DROP (see the note in moveTicket): on a
        // cross-column drop the source card unmounts before `dragend` fires, so relying
        // on the card's onDragEnd to clear `draggingTicketId` leaves the moved card stuck
        // with opacity-40 (white title looks gray in Dark Mode). Clearing at the drop —
        // which always fires on the target — fixes it without a page reload.
        setDraggingTicketId(null);
        if (!draggedId || draggedId === targetTicketId) return;
        const snapshot = data;
        if (!snapshot) return;

        let targetColId = null;
        for (const c of snapshot.columns) {
            if ((c.tickets || []).some((t) => t.id === targetTicketId)) { targetColId = c.id; break; }
        }
        let dragged = null;
        for (const c of snapshot.columns) {
            const f = (c.tickets || []).find((t) => t.id === draggedId);
            if (f) { dragged = f; break; }
        }
        if (!targetColId || !dragged) return;

        const targetCol = snapshot.columns.find((c) => c.id === targetColId);
        const without = (targetCol.tickets || []).filter((t) => t.id !== draggedId);
        const idx = without.findIndex((t) => t.id === targetTicketId);
        // 'before' lands at the target's index; 'after' lands just below it. The
        // before/after split (Ui movement Board #95) is what makes one-step moves work.
        const insertAt = idx < 0 ? without.length : (position === 'after' ? idx + 1 : idx);
        const newList = [...without];
        newList.splice(insertAt, 0, { ...dragged, column_id: targetColId });
        const orderedIds = newList.map((t) => t.id);

        // Optimistic: drop `dragged` from wherever it was, set the target column order.
        setData((d) => {
            if (!d) return d;
            const cols = d.columns.map((c) => ({ ...c, tickets: (c.tickets || []).filter((t) => t.id !== draggedId) }));
            return { ...d, columns: cols.map((c) => (c.id === targetColId ? { ...c, tickets: newList } : c)) };
        });

        const moves = orderedIds.map((tid, i) => ({ ticket_id: tid, column_id: targetColId, position: i }));
        boardService.reorderTickets(moves).catch((err) => {
            alert(prefixedUserErrorMessage(t('brdFailedReorder', 'Failed to reorder'), err));
            load();
        });
    };

    // Manual horizontal column reordering (Moving columns Boards #103): dropping a column
    // before/after another reorders them; persisted via the bulk /columns/reorder endpoint.
    const handleColumnReorder = (draggedColId, targetColId, position = 'before') => {
        // Clear the drag flag on the terminal DROP (mirrors moveTicket/handleReorderDrop):
        // a reorder re-renders the columns, and relying only on the header's onDragEnd can
        // leave the moved column stuck dimmed (opacity-40) — clearing here is reliable.
        setDraggingColumnId(null);
        if (!draggedColId || draggedColId === targetColId) return;
        const snapshot = data;
        if (!snapshot) return;
        const cols = snapshot.columns || [];
        const without = cols.filter((c) => c.id !== draggedColId);
        const dragged = cols.find((c) => c.id === draggedColId);
        if (!dragged) return;
        const idx = without.findIndex((c) => c.id === targetColId);
        if (idx < 0) return;
        const insertAt = position === 'after' ? idx + 1 : idx;
        const newCols = [...without];
        newCols.splice(insertAt, 0, dragged);
        const orderedIds = newCols.map((c) => c.id);

        setData((d) => (d ? { ...d, columns: newCols } : d));
        boardService.reorderColumns(boardId, orderedIds).catch((err) => {
            alert(prefixedUserErrorMessage(t('brdFailedReorderColumns', 'Failed to reorder columns'), err));
            load();
        });
    };

    const updateTicket = async (ticketId, updates) => {
        const updated = await boardService.updateTicket(ticketId, updates);
        // Keep the board cards in sync with the server's authoritative ticket so
        // chips (labels/assignees/title/cover/due) reflect the change immediately —
        // updateTicket used to skip this, so toggling a label only showed on the card
        // after the next board reload (Board labels #85).
        if (updated?.id) {
            setData((d) => {
                if (!d) return d;
                return {
                    ...d,
                    columns: d.columns.map((c) => ({
                        ...c,
                        tickets: (c.tickets || []).map((t) => t.id === ticketId ? { ...t, ...updated } : t),
                    })),
                };
            });
        }
        return updated;
    };

    // PAI Tick #213 — flip the completion tick. Reuses updateTicket's optimistic
    // setData merge so the card's strikethrough/dim + filled circle appear instantly.
    const toggleTicketComplete = (ticket) =>
        updateTicket(ticket.id, { is_completed: !ticket.is_completed });

    const deleteTicket = async (ticketId) => {
        if (!confirm(t('brdConfirmDeleteTicket', 'Delete this ticket?'))) return;
        // Optimistic removal — find which column owns it first
        let columnId = null;
        if (data) {
            for (const c of data.columns) {
                if ((c.tickets || []).some((t) => t.id === ticketId)) {
                    columnId = c.id; break;
                }
            }
        }
        if (columnId) removeTicketLocal(columnId, ticketId);
        setOpenTicketId(null);
        try {
            await boardService.deleteTicket(ticketId);
        } catch (err) {
            alert(prefixedUserErrorMessage(t('brdFailedDeleteTicketAlert', 'Failed to delete ticket'), err));
            await load();
        }
    };

    // Board Bugs #248 — comment mutations must re-hydrate the BOARD, not just the modal.
    // Exactly the Paperclip-bug shape (see refreshAfterAttachmentChange below): the
    // TicketDetailModal's own reload() refreshes its local ticket, so the thread updated
    // but the card's speech-bubble badge (driven by `comment_count`, which get_board_full
    // already serializes) stayed frozen until the board was closed and re-opened. A direct
    // load() after the API call is the immediate, reliable ping. load() never flips the
    // `loading` flag, so this is a silent refetch — no spinner flash, no modal remount.
    // Deliberately NOT awaited by the callers below: the modal chains its own
    // reload() on the promise these return, and serializing a whole-board GET in
    // front of that delays the thread's optimistic "sending..." placeholder being
    // replaced by the persisted comment. The badge is a passive indicator — it may
    // land a beat after the thread, but the user's own view must never wait on it.
    const refreshAfterCommentChange = () => {
        load().catch(() => { /* badge refresh is best-effort; never break the thread */ });
    };

    const addComment = async (ticketId, content) => {
        const res = await boardService.addComment(ticketId, content);
        refreshAfterCommentChange();
        return res;
    };

    const updateComment = async (commentId, content) => {
        const res = await boardService.updateComment(commentId, content);
        // An edit can't change the count, but keeping all three mutations symmetrical
        // means a future card-level indicator (e.g. "last activity") can never go
        // stale the way this one did.
        refreshAfterCommentChange();
        return res;
    };

    const deleteComment = async (commentId) => {
        const res = await boardService.deleteComment(commentId);
        refreshAfterCommentChange();
        return res;
    };

    // Paperclip bug: after ANY board-UI attach/detach, immediately re-hydrate the board so the
    // card's paperclip badge + count reflect reality (get_board_full already serializes
    // attachment_count). The TicketDetailModal's own reload() only refreshes the modal's local
    // ticket, not the underlying card — so without this the badge went stale in BOTH directions
    // (ghost icon after a removal; no icon after a board-UI attach). A direct load() is the
    // reliable, immediate "ping" — the chat-attach path already reaches the same board via its
    // omnis:ticket-attachment-added event listener (line ~1667). We don't re-dispatch that here
    // because this BoardView is the only view rendering the count and load() already refreshed it.
    const refreshAfterAttachmentChange = async () => {
        await load();
    };

    // Link an EXISTING document to a ticket (Select from File Manager / hash dedup #117).
    const attachExistingDocument = async (ticketId, documentId) => {
        await boardService.addAttachment(ticketId, { documentId });
        await refreshAfterAttachmentChange();
    };

    const attachFile = async (ticketId, file) => {
        // Hash dedup (#117): fingerprint the file's CONTENT (SHA-256); if an identical file
        // already exists in the user's File Manager, just LINK it — never upload a duplicate.
        try {
            const buf = await file.arrayBuffer();
            const digest = await crypto.subtle.digest('SHA-256', buf);
            const hash = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
            const existing = await documentService.findDocumentByHash(hash);
            if (existing?.id) {
                await boardService.addAttachment(ticketId, { documentId: existing.id });
                await refreshAfterAttachmentChange();
                return;
            }
        } catch { /* hashing unsupported / lookup failed → fall through to a normal upload */ }
        // Upload to the user's file manager — the file is indexed for RAG like any
        // other upload and linked to this ticket. If the filename collides with an
        // existing doc (global filename uniqueness → 409), retry once with a
        // disambiguated name so attaching never silently fails (Kanban Ticket Document
        // Attachments #92 — this was the "broken Attach File button").
        const upload = async (f) => {
            if (documentService.uploadDocumentToFolder) {
                try {
                    const folderResp = await documentService.findOrCreateConversationUploadsFolder?.();
                    if (folderResp?.id) return await documentService.uploadDocumentToFolder(f, folderResp.id);
                } catch { /* fall through to root upload */ }
            }
            return documentService.uploadDocument(f);
        };
        const uniquify = (name) => {
            const base = name || 'attachment';
            const dot = base.lastIndexOf('.');
            const stamp = `-${Date.now().toString(36)}`;
            return dot > 0 ? `${base.slice(0, dot)}${stamp}${base.slice(dot)}` : `${base}${stamp}`;
        };

        let uploaded;
        try {
            uploaded = await upload(file);
        } catch (err) {
            if (err?.status === 409 || err?.code === 'duplicate_filename') {
                const renamed = new File([file], uniquify(file.name), { type: file.type });
                uploaded = await upload(renamed);
            } else {
                throw err;
            }
        }
        // /api/documents/upload returns { type:'single', document:{ id } }; the folder
        // upload returns the document directly. Handle both shapes — reading only
        // `uploaded.id`/`document_id` was why attaching silently no-op'd (#92).
        const docId = uploaded?.document?.id || uploaded?.id || uploaded?.document_id;
        if (!docId) throw new Error('Upload did not return a document id');
        await boardService.addAttachment(ticketId, { documentId: docId });
        await refreshAfterAttachmentChange();
    };

    const removeAttachment = async (attachmentId) => {
        await boardService.removeAttachment(attachmentId);
        await refreshAfterAttachmentChange();
    };

    const setCover = async (ticketId, documentId) => {
        await boardService.updateTicket(ticketId, { cover_document_id: documentId });
    };

    // Member management is now owned by the shared <SharedSpaceDialog> (#194),
    // which calls boardService directly and refreshes via onChanged={load}.

    // ── Board settings: rename / delete (Manual Board deletion, rename and Menu #101) ──
    const renameBoard = async (name) => {
        const trimmed = (name || '').trim();
        if (!trimmed || trimmed === data?.board?.name) { setRenaming(false); return; }
        try {
            await boardService.updateBoard(boardId, { name: trimmed });
            await load();
            window.dispatchEvent(new CustomEvent('omnis:board-updated', { detail: { board_id: boardId } }));
        } catch (err) {
            alert(prefixedUserErrorMessage(t('brdFailedRenameBoard', 'Failed to rename board'), err));
        } finally {
            setRenaming(false);
        }
    };

    const deleteBoard = async () => {
        try {
            await boardService.deleteBoard(boardId);
            window.dispatchEvent(new CustomEvent('omnis:board-updated', { detail: { board_id: boardId } }));
            setConfirmingDelete(false);
            onBack?.();
        } catch (err) {
            alert(prefixedUserErrorMessage(t('brdFailedDeleteBoardAlert', 'Failed to delete board'), err));
        }
    };

    // #274 Leave Board: self-removal via the self-leave branch of the member-DELETE
    // route. Errors (409 member_via_team, 400 creator/last-owner) surface INSIDE
    // the modal — the BE is the authority on who may leave.
    const leaveBoard = async () => {
        if (leaving) return;
        setLeaving(true);
        setLeaveError('');
        try {
            await boardService.removeMember(boardId, user?.id);
            window.dispatchEvent(new CustomEvent('omnis:board-updated', { detail: { board_id: boardId } }));
            setConfirmingLeave(false);
            onBack?.();
        } catch (err) {
            setLeaveError(userErrorMessage(err, t));
        } finally {
            setLeaving(false);
        }
    };

    // ── Board label repository (Board labels #85) ─────────────────────
    const createLabel = async (name, color) => {
        const created = await boardService.createLabel(boardId, { name, color });
        await load();
        return created; // so LabelPicker's "Create & Add" can assign it (#113)
    };
    const updateLabel = async (labelId, updates) => {
        await boardService.updateLabel(labelId, updates);
        await load();
    };
    const deleteLabel = async (labelId) => {
        await boardService.deleteLabel(labelId);
        await load();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-[15px] text-pai-meta">
                {t('brdLoadingBoard', 'Loading board...')}
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="p-7 text-[15px] text-pai-red">{error || t('brdBoardNotFound', 'Board not found.')}</div>
        );
    }

    const { board, columns, labels, members } = data;
    // Role Authority / "Invisible UI" (#195): the OG Creator is the original owner.
    // Only they see Delete + can manage the Owner tier.
    //
    // `user` arrives from an ASYNC profile fetch (useAuth), so it is null for the
    // first render(s) of a freshly-mounted board. Without `identityKnown` the
    // comparison silently read false while the id was merely unknown, which
    // INVERTED the destructive affordances on your own board for ~1-3s: Delete
    // hidden, Leave offered and clickable (#274 re-open — reproduced with
    // identical ids on both sides). Unknown identity is not "not the creator".
    const identityKnown = !!user?.id;
    const isCreator = identityKnown && !!board?.created_by && board.created_by === user?.id;
    const canLeave = identityKnown && !isCreator;

    // In-board ticket search (#112): filter each column's tickets by title/description.
    const q = search.trim().toLowerCase();
    const visibleColumns = q
        ? columns.map((c) => ({
            ...c,
            tickets: (c.tickets || []).filter(
                (tk) => (tk.title || '').toLowerCase().includes(q)
                    || (tk.description || '').toLowerCase().includes(q),
            ),
        }))
        : columns;

    const [deleteWarnPre, deleteWarnPost] = tSplit(t, 'brdDeleteBoardWarning', '{name} and all its columns, tickets, and comments will be permanently deleted. This cannot be undone.', 'name');
    const [leaveWarnPre, leaveWarnPost] = tSplit(t, 'brdLeaveBoardWarning', 'Are you sure you want to exit {name}? You will lose access to this board and this action cannot be undone — only a board owner can invite you back.', 'name');

    return (
        <div className="flex-1 flex flex-col h-full min-h-0">
            {/* Single-row header (#112): [back] [symbol] [name] [search] … [members] [expand] [close] */}
            <div
                ref={headerRowRef}
                data-compact={compactHeader ? 'true' : 'false'}
                className={`flex items-center gap-2.5 py-4 border-b border-white/[0.06] flex-shrink-0 ${
                    compactHeader ? 'px-3' : 'px-6'
                }`}
            >
                {/* Title cluster (#222): back + icon + name as ONE shrinkable, truncating
                    unit — no fixed dead space. It caps tighter while the search is focused so
                    the search box can reclaim the width (long names truncate instead of the
                    search overlapping the member controls on the right).
                    COMPACT: the floor drops to the back button alone and the cap becomes an
                    ABSOLUTE width — a percentage of an already-too-small row leaves the search
                    nothing, which is what made the row overflow in the first place. */}
                <div
                    className={`flex items-center gap-2 transition-[max-width] duration-200 ease-out ${
                        compactHeader
                            ? `min-w-[2rem] ${searchFocused ? 'max-w-[2.5rem]' : 'max-w-[6rem]'}`
                            : `min-w-[7rem] ${searchFocused ? 'max-w-[20%]' : 'max-w-[42%]'}`
                    }`}
                >
                    <button
                        onClick={onBack}
                        className="pai-icon-btn w-8 h-8 shrink-0"
                        title={t('brdBackToBoards', 'Back to boards')}
                    >
                        <ChevronLeft size={18} />
                    </button>
                    {!compactHeader && <LayoutGrid size={20} className="text-white shrink-0" />}
                    {renaming ? (
                        <input
                            autoFocus
                            data-testid="board-rename-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            onBlur={() => renameBoard(renameValue)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') renameBoard(renameValue);
                                if (e.key === 'Escape') setRenaming(false);
                            }}
                            className="pai-input text-[22px] font-extrabold tracking-[-0.025em] px-2 py-0.5 min-w-0 flex-1"
                        />
                    ) : (
                        <h3 className="text-[22px] font-extrabold tracking-[-0.025em] text-white truncate min-w-0 flex-1">
                            {board.name}
                        </h3>
                    )}
                </div>
                {/* Search — fills the space the title cluster leaves and visibly EXPANDS on
                    focus (title yields via its tighter cap). gap-2 on the row guarantees a
                    persistent margin between the search box and the member controls. */}
                <div className="relative flex-1 min-w-0 overflow-hidden">
                    {/* COMPACT drops the leading magnifier: its 40px of reserved padding is a
                        HARD FLOOR on the input's border box (padding renders even at width 0),
                        so at narrow widths the icon was buying nothing and costing everything. */}
                    {!compactHeader && (
                        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none z-10" />
                    )}
                    <input
                        data-testid="board-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        placeholder={t('brdSearchInBoard', 'Search in Board')}
                        className={`pai-input w-full min-w-0 text-[14.5px] ${
                            compactHeader ? 'h-[38px] px-2.5' : 'h-[42px] pl-10 pr-3.5'
                        }`}
                    />
                </div>
                <div className={`flex items-center flex-shrink-0 ${compactHeader ? 'gap-1' : 'gap-1.5'}`}>
                    <button
                        data-testid="board-header-members"
                        onClick={() => setShowShare(true)}
                        // Kept compact (not the 42px pill of the handoff's wide layout) so
                        // the single-row header still fits a narrow docked split panel.
                        className={`flex items-center gap-1.5 text-[13.5px] font-semibold rounded-ctl border border-pai text-white hover:bg-white/[0.07] hover:border-pai-strong transition-colors flex-shrink-0 ${
                            compactHeader ? 'h-8 px-2' : 'h-[38px] px-3'
                        }`}
                        title={t('brdBoardMembers', 'Board members')}
                    >
                        <Users size={16} />
                        <span className="tabular-nums">{members.length}</span>
                    </button>
                    {/* Board settings kebab (Manual Board deletion, rename and Menu #101):
                        Manage members / Rename board / Delete board (owner-gated). */}
                    <div className="relative" ref={boardMenuRef}>
                        <button
                            data-testid="board-menu-button"
                            onClick={() => setBoardMenuOpen((v) => !v)}
                            aria-label={t('brdBoardSettings', 'Board settings')}
                            title={t('brdBoardSettings', 'Board settings')}
                            className={`pai-icon-btn flex-shrink-0 ${compactHeader ? 'w-8 h-8' : 'w-[38px] h-[38px]'}`}
                        >
                            <MoreHorizontal size={16} />
                        </button>
                        {boardMenuOpen && (
                            <div className="absolute right-0 top-[46px] z-30 w-52 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm py-1.5">
                                {/* #274 menu cleanup: the kebab's members entry is OWNERS-only
                                    ("Manage Members" — a management action, #195). For plain
                                    members it was a redundant link to the read-only roster the
                                    header Users icon already opens, so it's gone. */}
                                {board.viewer_role === 'owner' && (
                                    <button
                                        data-testid="board-menu-members"
                                        onClick={() => { setBoardMenuOpen(false); setShowShare(true); }}
                                        className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white flex items-center gap-2.5 transition-colors"
                                    >
                                        <Users size={14} /> {t('brdManageMembers', 'Manage Members')}
                                    </button>
                                )}
                                {/* Board Settings (Rename/Color/Columns) — any Owner. */}
                                {board.viewer_role === 'owner' && (
                                    <button
                                        data-testid="board-menu-rename"
                                        onClick={() => { setBoardMenuOpen(false); setRenameValue(board.name || ''); setRenaming(true); }}
                                        className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-secondary hover:bg-white/[0.07] hover:text-white flex items-center gap-2.5 transition-colors"
                                    >
                                        <Edit2 size={14} /> {t('brdRenameBoard', 'Rename board')}
                                    </button>
                                )}
                                {/* Delete — OG Creator ONLY (completely absent for other Owners/Members). */}
                                {isCreator && (
                                    <button
                                        data-testid="board-menu-delete"
                                        onClick={() => { setBoardMenuOpen(false); setConfirmingDelete(true); }}
                                        className="w-full text-left px-3.5 py-2 text-[13.5px] font-medium text-pai-red hover:bg-pai-red/[0.14] flex items-center gap-2.5 transition-colors"
                                    >
                                        <Trash2 size={14} /> {t('brdDeleteBoardMenuItem', 'Delete board')}
                                    </button>
                                )}
                                {/* #274 Leave Board — ALWAYS rendered, so the menu never looks
                                    broken. The OG Creator still cannot leave (a creator-less
                                    board would be undeletable, deletion being creator-only per
                                    #195) — for them it renders DISABLED with a teaching note
                                    pointing at Delete. That absence WAS the re-open ("the button
                                    doesnt appear"): every board the reporter holds is one they
                                    created, so the entry existed nowhere they could look.
                                    Also disabled while identity is still unknown (see above), so
                                    an enabled Leave is never offered on your own board.
                                    The BE guard is unchanged — it still 400s creator_cannot_leave. */}
                                <button
                                    data-testid="board-menu-leave"
                                    disabled={!canLeave}
                                    aria-disabled={!canLeave}
                                    title={isCreator ? t('brdCreatorCantLeave', "As the board's creator you can't leave — delete the board instead.") : undefined}
                                    onClick={() => {
                                        if (!canLeave) return;
                                        setBoardMenuOpen(false); setLeaveError(''); setConfirmingLeave(true);
                                    }}
                                    className={`w-full text-left px-3.5 py-2 text-[13.5px] font-medium flex items-center gap-2.5 transition-colors ${
                                        canLeave
                                            ? 'text-pai-red hover:bg-pai-red/[0.14]'
                                            : 'text-pai-faint cursor-not-allowed'
                                    }`}
                                >
                                    <LogOut size={14} /> {t('brdLeaveBoardMenuItem', 'Leave Board')}
                                </button>
                                {isCreator && (
                                    <p
                                        data-testid="board-menu-leave-note"
                                        className="px-3.5 pb-2 -mt-1 text-[11.5px] leading-snug text-pai-meta"
                                    >
                                        {t('brdCreatorCantLeave', "As the board's creator you can't leave — delete the board instead.")}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    {onToggleExpand && (
                        <button
                            onClick={onToggleExpand}
                            className={`pai-icon-btn flex-shrink-0 ${compactHeader ? 'w-8 h-8' : 'w-[38px] h-[38px]'}`}
                            title={mode === 'expanded' ? t('brdCollapseSplitView', 'Collapse to split view') : t('brdExpandCoverChat', 'Expand to cover chat')}
                        >
                            {mode === 'expanded' ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                        </button>
                    )}
                    {onClose && (
                        <button
                            onClick={onClose}
                            className={`pai-icon-btn flex-shrink-0 ${compactHeader ? 'w-8 h-8' : 'w-[38px] h-[38px]'}`}
                            title={t('brdClosePanelEsc', 'Close panel (Esc)')}
                        >
                            <X size={18} />
                        </button>
                    )}
                </div>
            </div>

            {/* Kanban scroller */}
            <div ref={kanbanScrollerRef} data-testid="kanban-scroller" className="flex-1 overflow-x-auto overflow-y-hidden px-6 py-5">
                <div className="flex gap-[18px] h-full items-start">
                    {visibleColumns.map((c) => (
                        <ColumnReorderSlot
                            key={c.id}
                            columnId={c.id}
                            draggingColumnId={draggingColumnId}
                            onReorder={handleColumnReorder}
                        >
                            <Column
                                column={c}
                                tickets={c.tickets || []}
                                dragging={draggingColumnId === c.id}
                                onAddTicket={addTicket}
                                onOpenTicket={(t) => setOpenTicketId(t.id)}
                                onRenameColumn={renameColumn}
                                onDeleteColumn={deleteColumn}
                                onTicketDrop={moveTicket}
                                onReorderDrop={handleReorderDrop}
                                draggingTicketId={draggingTicketId}
                                onTicketDragStart={(t) => setDraggingTicketId(t.id)}
                                onTicketDragEnd={() => setDraggingTicketId(null)}
                                onColumnDragStart={(col) => setDraggingColumnId(col.id)}
                                onColumnDragEnd={() => setDraggingColumnId(null)}
                                onMoveColumn={(col) => setMovingColumn({ id: col.id, name: col.name })}
                                onMoveTicket={(t) => setMovingTicket(t)}
                                onToggleComplete={toggleTicketComplete}
                            />
                        </ColumnReorderSlot>
                    ))}
                    {newColumnComposing ? (
                        <form onSubmit={addColumn} className="w-[300px] flex-shrink-0 rounded-card border border-pai-subtle bg-white/[0.025] p-4">
                            <input
                                autoFocus
                                value={newColumnName}
                                onChange={(e) => setNewColumnName(e.target.value)}
                                onBlur={() => { if (!newColumnName.trim()) setNewColumnComposing(false); }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') addColumn();
                                    if (e.key === 'Escape') { setNewColumnName(''); setNewColumnComposing(false); }
                                }}
                                placeholder={t('brdColumnNamePlaceholder', 'Column name...')}
                                className="pai-input w-full px-3 py-2 text-[15px]"
                            />
                            <div className="mt-2.5 flex gap-2 justify-end">
                                <button type="button" onClick={() => { setNewColumnName(''); setNewColumnComposing(false); }} className="px-2.5 py-1 text-[13px] font-medium text-pai-secondary hover:text-white hover:bg-white/[0.07] rounded-seg transition-colors">{t('commonCancel', 'Cancel')}</button>
                                <button type="submit" disabled={!newColumnName.trim()} className="px-3 py-1 text-[13px] font-semibold bg-white text-pai-void rounded-seg hover:bg-white/[0.88] disabled:opacity-50 transition-colors">{t('commonAdd', 'Add')}</button>
                            </div>
                        </form>
                    ) : (
                        <button
                            onClick={() => setNewColumnComposing(true)}
                            className="w-[300px] flex-shrink-0 h-14 flex items-center justify-center gap-2 text-[14px] font-semibold text-white/50 hover:text-white bg-transparent hover:bg-white/[0.03] border border-dashed border-white/20 hover:border-white/[0.45] rounded-card transition-colors"
                        >
                            <Plus size={17} /> {t('brdAddColumn', 'Add column')}
                        </button>
                    )}
                </div>
            </div>

            {openTicketId && (
                <TicketDetailModal
                    ticketId={openTicketId}
                    members={members}
                    labels={labels}
                    onClose={() => setOpenTicketId(null)}
                    onUpdate={updateTicket}
                    onDelete={deleteTicket}
                    onAddComment={addComment}
                    onUpdateComment={updateComment}
                    onDeleteComment={deleteComment}
                    onAttach={attachFile}
                    onAttachExisting={attachExistingDocument}
                    onRemoveAttachment={removeAttachment}
                    onSetCover={setCover}
                    onCreateLabel={createLabel}
                    onUpdateLabel={updateLabel}
                    onDeleteLabel={deleteLabel}
                />
            )}

            {showShare && (
                <SharedSpaceDialog
                    surface="board"
                    entityId={boardId}
                    viewerRole={board.viewer_role}
                    isCreator={isCreator}
                    viewerUserId={user?.id}
                    onClose={() => setShowShare(false)}
                    onChanged={load}
                />
            )}

            {/* Move a column to another board (Manual column move #110) */}
            {movingColumn && (
                <ColumnMoveDialog
                    column={movingColumn}
                    currentBoardId={boardId}
                    onClose={() => setMovingColumn(null)}
                    onMoved={() => {
                        setMovingColumn(null);
                        load();
                        window.dispatchEvent(new CustomEvent('omnis:board-updated', { detail: { board_id: boardId } }));
                    }}
                />
            )}

            {/* Move a single ticket to any board/column/position (Manual Ticket movement #136) */}
            {movingTicket && (
                <TicketMoveDialog
                    ticket={movingTicket}
                    currentBoardId={boardId}
                    onClose={() => setMovingTicket(null)}
                    onMoved={({ targetBoardId } = {}) => {
                        setMovingTicket(null);
                        load(); // re-hydrate the open board (the ticket may have left it)
                        // Refresh the boards-list counts + any other open board view.
                        window.dispatchEvent(new CustomEvent('omnis:board-updated', { detail: { board_id: boardId } }));
                        if (targetBoardId && targetBoardId !== boardId) {
                            window.dispatchEvent(new CustomEvent('omnis:ticket-updated', { detail: { board_id: targetBoardId } }));
                            window.dispatchEvent(new CustomEvent('omnis:board-updated', { detail: { board_id: targetBoardId } }));
                        }
                    }}
                />
            )}

            {/* Mandatory delete confirmation (Manual Board deletion #101).
                PORTALED to <body> (#274 UI fix, the #225 pattern): the panel sits
                under transformed ancestors, so an in-tree `fixed` overlay was
                positioned/clipped by the PANEL instead of centering on screen. */}
            {confirmingDelete && (
                <Modal onClose={() => setConfirmingDelete(false)} zIndex="z-[70]" size="sm" closeOnBackdrop={false} scrimTestId="board-delete-confirm" cardClassName="p-7">
                        <h2 className="text-[22px] font-bold tracking-[-0.02em] text-white mb-2.5">{t('brdDeleteBoardQuestion', 'Delete board?')}</h2>
                        <p className="text-[15px] leading-[1.6] text-pai-secondary mb-6">
                            {deleteWarnPre}<span className="font-semibold text-white">{board.name}</span>{deleteWarnPost}
                        </p>
                        <div className="flex justify-end gap-2.5">
                            <button onClick={() => setConfirmingDelete(false)} className="pai-btn-ghost">{t('commonCancel', 'Cancel')}</button>
                            <button data-testid="board-delete-confirm-btn" onClick={deleteBoard} className="pai-btn-danger">{t('brdDeleteBoardMenuItem', 'Delete board')}</button>
                        </div>
                    </Modal>
            )}

            {/* #274 Leave Board — mandatory exit confirmation, centered + portaled
                to <body> so no transformed/overflow ancestor can clip it. */}
            {confirmingLeave && (
                <Modal onClose={() => { setConfirmingLeave(false); setLeaveError(''); }} zIndex="z-[70]" size="sm" closeOnBackdrop={false} scrimTestId="board-leave-confirm" cardClassName="p-7">
                        <h2 className="text-[22px] font-bold tracking-[-0.02em] text-white mb-2.5">{t('brdLeaveBoardQuestion', 'Leave board?')}</h2>
                        <p className="text-[15px] leading-[1.6] text-pai-secondary mb-6">
                            {leaveWarnPre}<span className="font-semibold text-white">{board.name}</span>{leaveWarnPost}
                        </p>
                        {leaveError && (
                            <p data-testid="board-leave-error" className="text-[13px] text-pai-red mb-4">
                                {leaveError}
                            </p>
                        )}
                        <div className="flex justify-end gap-2.5">
                            <button
                                data-testid="board-leave-cancel-btn"
                                onClick={() => { setConfirmingLeave(false); setLeaveError(''); }}
                                className="pai-btn-ghost"
                            >
                                {t('commonCancel', 'Cancel')}
                            </button>
                            <button
                                data-testid="board-leave-confirm-btn"
                                onClick={leaveBoard}
                                disabled={leaving}
                                className="pai-btn-danger disabled:opacity-50"
                            >
                                {leaving ? t('brdLeavingEllipsis', 'Leaving...') : t('brdLeaveBoardMenuItem', 'Leave Board')}
                            </button>
                        </div>
                    </Modal>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────
// Top-level panel (grid OR single board)
// ─────────────────────────────────────────────────────────────────────────

const BoardsPanel = (panelProps) => {
    const { t } = useLanguage();
    const [boards, setBoards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [showNew, setShowNew] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // Deep link: /boards?board=<id> opens that board directly (PAI Board Management #102 —
    // the chat board-deletion card's "Open Board" button). Consume the param once opened.
    useEffect(() => {
        const target = searchParams.get('board');
        if (target) {
            setSelectedId(target);
            const next = new URLSearchParams(searchParams);
            next.delete('board');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const loadBoards = useCallback(async () => {
        try {
            const resp = await boardService.listBoards();
            setBoards(resp.boards || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadBoards(); }, [loadBoards]);

    useEffect(() => {
        const refresh = () => loadBoards();
        window.addEventListener('omnis:board-created', refresh);
        window.addEventListener('omnis:board-updated', refresh);
        window.addEventListener('omnis:board-deleted', refresh);
        window.addEventListener('omnis:ticket-created', refresh);
        window.addEventListener('omnis:ticket-deleted', refresh);
        return () => {
            window.removeEventListener('omnis:board-created', refresh);
            window.removeEventListener('omnis:board-updated', refresh);
            window.removeEventListener('omnis:board-deleted', refresh);
            window.removeEventListener('omnis:ticket-created', refresh);
            window.removeEventListener('omnis:ticket-deleted', refresh);
        };
    }, [loadBoards]);

    // Cleanup Boards #496: no accent colour — the board colour was never rendered anywhere.
    const createBoard = async ({ name }) => {
        const board = await boardService.createBoard({ name });
        setShowNew(false);
        await loadBoards();
        if (board?.id) setSelectedId(board.id);
    };

    const titleNode = selectedId
        ? boards.find((b) => b.id === selectedId)?.name || t('brdBoardFallback', 'Board')
        : t('boards', 'Boards');

    return (
        <PanelShell
            title={titleNode}
            icon={<LayoutGrid size={16} />}
            {...panelProps}
            // Kanban Total Redesign #112: when a board is open, BoardView draws its
            // own single-row header (back + symbol + name + search + controls), so the
            // shared chrome header is suppressed to avoid a duplicate second row.
            noChromeHeader={!!selectedId}
        >
            {loading ? (
                <div className="px-7 py-6 text-[15px] text-pai-meta">{t('brdLoading', 'Loading...')}</div>
            ) : selectedId ? (
                <BoardView
                    boardId={selectedId}
                    onBack={() => setSelectedId(null)}
                    onClose={panelProps.onClose}
                    onToggleExpand={panelProps.onToggleExpand}
                    mode={panelProps.mode}
                />
            ) : (
                <BoardsGrid boards={boards} onOpen={(id) => setSelectedId(id)} onCreate={() => setShowNew(true)} />
            )}

            {showNew && (
                <NewBoardDialog onClose={() => setShowNew(false)} onCreate={createBoard} />
            )}
        </PanelShell>
    );
};

export default BoardsPanel;
